
import { Control, UIElementType, UEAttr, ModificationDescription, ImmutableHtmlNode, ImmutableHtmlElementNode, ExtensionBuilder, Block, BlockRenderer } from '@stripoinc/ui-editor-extensions';
import {
  SettingsPanelRegistry,
  SettingsPanelTab,
  SettingsTab,
  IconsRegistry
} from '@stripoinc/ui-editor-extensions';
import { Observable, lastValueFrom } from 'rxjs';
import { TemplateRef } from '@angular/core';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import $ from 'jquery';
import { NbDialogService, NbDialogRef } from '@nebular/theme';
import { ResizeImageModalComponent } from './resize-image-modal/resize-image-modal.component';
import { FireflyChoiceComponent } from 'src/app/deebr/component/firefly-choice/firefly-choice.component';
import { ImageSelectionService } from '../../../service/image-selection.service';
import { ImageToGifDialogComponent } from './image-to-gif-dialog/image-to-gif-dialog.component';
import { ImageAmbianceDialogComponent } from './image-ambiance-dialog/image-ambiance-dialog.component';
import { WriteOnImageComponent } from 'src/app/deebr/component/write-on-image/write-on-image.component';
import { WriteCtaOnImageComponent, buildCtaOptions } from 'src/app/deebr/component/write-cta-on-image/write-cta-on-image.component';
import { ToastService } from 'src/app/deebr/service/toast.service';



export class ClipboardService {
  private static storageKey = 'stripo_clipboard_content';

  static write(html: string): void {
    localStorage.setItem(this.storageKey, html);
  }

  static read(): string | null {
    return localStorage.getItem(this.storageKey);
  }

  static clear(): void {
    localStorage.removeItem(this.storageKey);
  }
}

/** Attribut porté par chaque bloc produit : id du template Deebr dont il provient. */
export const DEEBR_TEMPLATE_ID_ATTR = 'data-deebr-template-id';

// Balise ouvrante avec ses attributs (valeurs entre guillemets tolérant '>' à l'intérieur).
const OPEN_TAG_RE = /<([a-zA-Z][\w:-]*)((?:\s+[^\s"'<>\/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'<>`]+))?)*)\s*(\/?)>/g;

/**
 * Tamponne le template d'origine sur chaque balise portant deebr-product-id qui ne l'a pas
 * encore. La personnalisation (backend) s'en sert pour retrouver le slot NON REMPLI du bloc,
 * même une fois collé dans un email construit sur un autre template.
 *
 * Travaille au niveau chaîne (pas de re-sérialisation DOM) : la bande copiée reste identique
 * octet pour octet, seul l'attribut est ajouté. Un bloc déjà annoté (collé auparavant depuis
 * un autre email) garde son origine.
 */
export function annotateTemplateId(html: string, templateId: number | null | undefined): string {
  if (!templateId || !html) return html;
  const hasOrigin = new RegExp(`(?:^|\\s)${DEEBR_TEMPLATE_ID_ATTR}\\s*=`);
  return html.replace(OPEN_TAG_RE, (match, tag, attrs, slash) => {
    if (!/(?:^|\s)deebr-product-id\s*=/.test(attrs)) return match;
    if (hasOrigin.test(attrs)) return match;
    return `<${tag}${attrs} ${DEEBR_TEMPLATE_ID_ATTR}="${templateId}"${slash}>`;
  });
}

/** Préfixe des clés du presse-papier NATIF de Stripo (Ctrl+C sur un bloc / icône copier). */
const STRIPO_PORTABLE_CLIPBOARD_PREFIX = 'stripo-editor:portable-element:';

/**
 * Purge le presse-papier natif de Stripo avant chaque initialisation d'éditeur.
 *
 * Quand une entrée `stripo-editor:portable-element:v1:*` valide existe dans le
 * localStorage, Stripo (UIEditor) lance au démarrage un marquage récursif de
 * tout le document comme cible de dépôt (`markAsDroppable`) qui, sur nos
 * templates, ne rend jamais la main : thread bloqué, mémoire qui grimpe
 * (+1,3 Mo/s observé), onglet tué par le navigateur. L'entrée expire au bout
 * de 2 h, d'où le caractère « aléatoire » du bug : l'éditeur gelait pendant les
 * 2 h suivant une copie native, puis remarchait seul. Vider les données du site
 * la supprimait aussi — c'était la « manip des cookies ».
 *
 * Reproduit à la demande le 2026-09-02 en réinjectant cette seule clé.
 *
 * Coût : le Ctrl+C natif fait dans un éditeur ne survit pas à l'ouverture d'un
 * autre. Le copier-coller *dans* un même éditeur n'est pas affecté, et nos
 * boutons « Copier / Coller la bande » (clé `stripo_clipboard_content`,
 * distincte) restent le moyen de copier d'un email à l'autre.
 *
 * @returns le nombre d'entrées supprimées
 */
export function purgeStripoPortableClipboard(): number {
  let removed = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STRIPO_PORTABLE_CLIPBOARD_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      localStorage.removeItem(key);
      removed++;
    }
    if (removed > 0) {
      console.info(`[Deebr] Presse-papier natif Stripo purgé avant init (${removed} entrée(s)).`);
    }
  } catch (e) {
    // localStorage peut lever (mode privé restrictif, quota) : ne jamais bloquer l'init pour ça.
    console.warn('[Deebr] Purge du presse-papier Stripo impossible :', e);
  }
  return removed;
}

export class GlobalExtensionState {
  static selectedNode: ImmutableHtmlNode | null = null;
}

export class CopyPasteControl extends Control {
  private selectedNode: ImmutableHtmlNode | null = null;
  public toastService?: ToastService;
  private clipboardSyncSetup = false;
  private lastCopiedHtml: string | null = null;
  /** Template Deebr de l'email ouvert (null : email importé / sans template). Posé par le composant à l'init. */
  public static currentTemplateId: number | null = null;

  override getId() {
    return 'copy-paste-control';
  }

  override getTemplate() {
    return `
      <div style="display:flex; gap:10px; margin-left:20px;margin-bottom:10px;margin-top:10px">
          <${UIElementType.BUTTON}
              ${UEAttr.BUTTON.caption}="Copier la bande"
              ${UEAttr.BUTTON.name}="copyBlock"
          ></${UIElementType.BUTTON}>
          
          <${UIElementType.BUTTON}
              ${UEAttr.BUTTON.caption}="Coller la bande"
              ${UEAttr.BUTTON.name}="pasteBlock"
          ></${UIElementType.BUTTON}>
      </div>
    `;
  }



  private setupClipboardSync() {
    if (this.clipboardSyncSetup) return;
    this.clipboardSyncSetup = true;

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== 'stripo_clipboard_content') return;

      this.lastCopiedHtml = typeof event.newValue === 'string'
        ? event.newValue
        : null;
    });
  }

  private setBusy(buttonName: 'copyBlock' | 'pasteBlock', isBusy: boolean, busyCaption: string, idleCaption: string, delay = 0) {
    const caption = isBusy ? busyCaption : idleCaption;

    this.api.setUIEAttribute(buttonName, UEAttr.BUTTON.caption, caption);
    this.api.setUIEAttribute(buttonName, UEAttr.BUTTON.disabled, isBusy);

    setTimeout(() => {
      this.api.setUIEAttribute(buttonName, UEAttr.BUTTON.caption, caption);
      this.api.setUIEAttribute(buttonName, UEAttr.BUTTON.disabled, isBusy);
    }, delay);
  }




  override onRender() {
    this.setupClipboardSync();

    this.api.onValueChanged('copyBlock', (newValue, oldValue) => {
      if (!newValue) return;
      if (newValue === oldValue) return;

      // ✅ state UI visible avant action
      this.setBusy('copyBlock', true, 'Copie…', 'Copier la bande');

      requestAnimationFrame(() => {
        this.api.updateValues({ copyBlock: null });
        void this.copySelection(this.selectedNode);
      });
    });

    this.api.onValueChanged('pasteBlock', async (newValue, oldValue) => {
      if (!newValue || newValue === oldValue) return;

      this.setBusy('pasteBlock', true, 'Collage…', 'Coller la bande');
      this.api.updateValues({ pasteBlock: null });

      await new Promise(resolve => setTimeout(resolve, 0));

      await this.pasteSelection(this.selectedNode);
    });

  }



  // We don't need onTemplateNodeUpdated anymore for the repair
  override onTemplateNodeUpdated(node: ImmutableHtmlNode) {
    this.selectedNode = node;
    GlobalExtensionState.selectedNode = node;
  }

  /**
   * Retourne la table module (es-content / es-header / es-footer)
   */
  private getSelectedModuleTable(node: ImmutableHtmlNode): ImmutableHtmlElementNode | null {
    const table =
      node.closest('table.es-content') ||
      node.closest('table.es-header') ||
      node.closest('table.es-footer');

    if (!table || table.getType() !== 'element') return null;
    return table.asElement();
  }


  private async copySelection(nodeSnapshot: ImmutableHtmlNode | null) {
    try {
      if (!nodeSnapshot) return;

      const table = this.getSelectedModuleTable(nodeSnapshot);
      if (!table) return;

      // Origine des blocs produit : tamponnée à la COPIE (l'email source connaît son template).
      const html = annotateTemplateId(table.getOuterHTML(), CopyPasteControl.currentTemplateId);
      if (!html?.trim()) return;

      this.lastCopiedHtml = html;
      ClipboardService.write(html);

      this.toastService?.successOnce('copyPasteToast', 'Email Creator', 'Structure copiée', 1000);
    } finally {
      setTimeout(() => this.setBusy('copyBlock', false, 'Copie…', 'Copier la bande'), 400);
    }
  }

  private async pasteSelection(nodeSnapshot: ImmutableHtmlNode | null) {
    try {
      if (!nodeSnapshot) return;

      const anchorTable = this.getSelectedModuleTable(nodeSnapshot);
      if (!anchorTable) return;

      const htmlFromClipboard = (ClipboardService.read() || this.lastCopiedHtml)?.trim();
      if (!htmlFromClipboard) {
        this.toastService?.error('Email Creator', 'Aucune structure copiée');
        return;
      }

      // Pas d'annotation au COLLAGE : tamponner le template de l'email cible affirmerait une fausse
      // origine. Un bloc sans data-deebr-template-id retombe côté backend sur le template de l'email.
      const pastedTableHtml = htmlFromClipboard.startsWith('<table')
        ? htmlFromClipboard
        : `<table>${htmlFromClipboard}</table>`;

      const parent = anchorTable.parent();
      if (!parent || parent.getType() !== 'element') return;
      const parentEl = parent.asElement();

      const modifier = this.api.getDocumentModifier();

      // ── Préserver le masquage desktop/mobile des AUTRES bandes ────────────────
      // Coller reconstruit tout l'innerHTML du parent (seul moyen de positionner
      // la bande APRÈS l'ancre). Or l'état "masqué" vit dans le modèle Stripo, PAS
      // dans getOuterHTML() → le re-parse l'effacerait sur tous les frères ("ils
      // sautent tous").
      //
      // Parade en 3 temps :
      //  1. on marque chaque <tr> du parent d'un id temporaire (lui EST dans le
      //     HTML, donc survit au re-parse) ;
      //  2. on lit le TEMPLATE sérialisé (getTemplateData) qui, lui, encode le
      //     masquage en classes es-desk-hidden / es-mobile-hidden → on mappe
      //     id → 'desktop'|'mobile' ;
      //  3. après le collage, on réapplique le masquage sur les <tr> retrouvés par
      //     leur id, puis on retire les ids.
      const TMP_ID_ATTR = 'deebr-tmp-hidden-id';

      // 1) marquer tous les <tr> du parent
      let counter = 0;
      const trNodes = parentEl.querySelectorAll('tr');
      for (const tr of trNodes) {
        if (tr.getType() !== 'element') continue;
        modifier.modifyHtml(tr).setAttribute(TMP_ID_ATTR, String(counter++));
      }
      modifier.apply(new ModificationDescription('Tag rows before paste'));

      // 2) lire le masquage depuis le template sérialisé (asynchrone)
      const hiddenById = await this.readHiddenStatesFromTemplate(TMP_ID_ATTR);

      // 3) collage : reconstruit l'innerHTML du parent avec la bande après l'ancre
      //    (anchorHtml relu APRÈS le marquage pour matcher le parentOuterHtml courant)
      const anchorHtml = anchorTable.getOuterHTML();
      const parentOuterHtml = parentEl.getOuterHTML();
      const openTagEnd = parentOuterHtml.indexOf('>') + 1;
      const closeTagStart = parentOuterHtml.lastIndexOf('</');
      const innerHtml = parentOuterHtml.substring(openTagEnd, closeTagStart);

      if (innerHtml.includes(anchorHtml)) {
        const newInnerHtml = innerHtml.replace(anchorHtml, `${anchorHtml}${pastedTableHtml}`);
        modifier.modifyHtml(parentEl).setInnerHtml(newInnerHtml);
      } else {
        modifier.modifyHtml(parentEl).append(pastedTableHtml);
      }
      modifier.apply(new ModificationDescription('Paste module after selected module'));

      // ── Après le rebuild DOM : réappliquer le masquage + nettoyer les ids ──────
      setTimeout(() => {
        try {
          this.restoreHiddenStates(parentEl, TMP_ID_ATTR, hiddenById);
        } catch (e) {
          console.warn('[Deebr] Failed to restore hidden states:', e);
        }
        try {
          this.repairDynamicBlockClasses(parentEl);
        } catch (e) {
          console.warn('[Deebr] Failed to repair dynamic block classes:', e);
        }
      }, 500);

      this.toastService?.success('Email Creator', 'Structure collée avec succès');
    } finally {
      setTimeout(() => this.setBusy('pasteBlock', false, 'Collage…', 'Coller la bande'), 1000);
    }
  }

  private repairDynamicBlockClasses(rootNode: ImmutableHtmlNode) {
    // Traverse upwards to get the highest possible root, just in case
    let root = rootNode;
    while (root.parent()) {
      try { root = root.parent(); } catch { break; }
    }

    // Find all dynamic blocks
    const dynamicBlocks = root.querySelectorAll('[deebr-data-dynamic="true"]');
    if (!dynamicBlocks || dynamicBlocks.length === 0) return;

    const modifier = this.api.getDocumentModifier();
    let needsApply = false;

    for (const block of dynamicBlocks) {
      // Find all TRs inside this dynamic block
      const trs = block.querySelectorAll('tr');
      for (const tr of trs) {
        if (tr.getType() !== 'element') continue;
        const trEl = tr.asElement();
        const html = trEl.getOuterHTML();

        // Desktop block: contains @desk image URL → should be hidden on mobile
        if (html.includes('@desk') && !html.includes('@mob')) {
          if (!trEl.hasClass('es-mobile-hidden')) {
            modifier.modifyHtml(tr).setClass('es-mobile-hidden');
            needsApply = true;
          }
        }
        // Mobile block: contains @mob image URL → should be hidden on desktop
        if (html.includes('@mob') && !html.includes('@desk')) {
          if (!trEl.hasClass('es-desk-hidden')) {
            modifier.modifyHtml(tr).setClass('es-desk-hidden');
            needsApply = true;
          }
        }
      }
    }

    if (needsApply) {
      modifier.apply(new ModificationDescription('Restore dynamic block responsive classes'));
      console.log('[Deebr] Repaired dynamic block responsive classes');
    }
  }

  /**
   * Lit le TEMPLATE sérialisé (getTemplateData) — qui, contrairement à
   * getOuterHTML, encode le masquage en classes es-desk-hidden / es-mobile-hidden —
   * et retourne une map `id temporaire → 'desktop' | 'mobile'` pour les <tr>
   * marqués juste avant.
   */
  private readHiddenStatesFromTemplate(tmpIdAttr: string): Promise<Map<string, 'desktop' | 'mobile'>> {
    return new Promise((resolve) => {
      const map = new Map<string, 'desktop' | 'mobile'>();
      const actionsApi = (window as any).StripoEditorApi?.actionsApi;
      if (!actionsApi?.getTemplateData) {
        console.warn('[Deebr] actionsApi.getTemplateData indisponible → masquage non préservé');
        resolve(map);
        return;
      }
      try {
        actionsApi.getTemplateData((data: { html: string }) => {
          try {
            const doc = new DOMParser().parseFromString(data?.html || '', 'text/html');
            doc.querySelectorAll('.es-desk-hidden').forEach((el) => {
              const id = el.getAttribute(tmpIdAttr);
              if (id != null) map.set(id, 'desktop');
            });
            doc.querySelectorAll('.es-mobile-hidden').forEach((el) => {
              const id = el.getAttribute(tmpIdAttr);
              if (id != null) map.set(id, 'mobile');
            });
          } catch (e) {
            console.warn('[Deebr] parsing template échoué:', e);
          }
          resolve(map);
        });
      } catch (e) {
        console.warn('[Deebr] getTemplateData a jeté:', e);
        resolve(map);
      }
    });
  }

  /**
   * Réapplique le masquage desktop/mobile sur les <tr> marqués avant le collage
   * (retrouvés par leur id temporaire, qui a survécu au re-parse HTML), puis
   * retire ces ids. Writer : setHiddenElementState (officiel, sur le modifier) si
   * dispo, sinon fallback sur les classes es-desk-hidden / es-mobile-hidden.
   */
  private restoreHiddenStates(
    parentEl: ImmutableHtmlElementNode,
    tmpIdAttr: string,
    hiddenById: Map<string, 'desktop' | 'mobile'>,
  ) {
    const tagged = parentEl.querySelectorAll(`[${tmpIdAttr}]`);
    if (!tagged || tagged.length === 0) return;

    const modifier = this.api.getDocumentModifier();

    for (const node of tagged) {
      if (node.getType() !== 'element') continue;
      const el = node.asElement();
      const id = el.getAttribute(tmpIdAttr);
      const state = id != null ? hiddenById.get(id) : undefined;

      if (state) {
        const mod = modifier.modifyHtml(node) as any;
        // writer officiel si dispo, sinon fallback classes
        if (typeof mod.setHiddenElementState === 'function') {
          mod.setHiddenElementState(state);
        } else {
          mod.setClass(state === 'desktop' ? 'es-desk-hidden' : 'es-mobile-hidden');
        }
      }
      modifier.modifyHtml(node).removeAttribute(tmpIdAttr);
    }

    modifier.apply(new ModificationDescription('Restore hidden states after paste'));
  }

}






/**
 * @param enableDynamic  déclare les contrôles de personnalisation
 * @param enableBackup   déclare le contrôle Backup. À n'activer QUE si
 *   `BackupControl` est réellement passé à l'ExtensionBuilder : déclarer dans le
 *   panneau un contrôle qui n'est pas fourni fait échouer Stripo à la résolution
 *   ("Failed to find control backup-control!") à l'ouverture des réglages d'un
 *   conteneur. Les deux doivent donc être pilotés par le même drapeau.
 */
export function createSettingsRegistryClass(enableDynamic: boolean, enableBackup: boolean = false) {
  return class CustomSettingsRegistry extends SettingsPanelRegistry {
    override registerBlockControls(blockControlsMap: Record<string, SettingsPanelTab[]>) {
      // Anciens contrôles Copy/Paste
      this.addControlsForBlock(blockControlsMap, 'STRIPE', [
        { controlId: 'copy-paste-control', position: 0 },
      ]);

      if (enableDynamic) {
        this.addControlsForBlock(blockControlsMap, 'CONTAINER', [
          { controlId: 'dynamic-control', position: 0 },
        ]);
        this.addControlsForBlock(blockControlsMap, 'STRUCTURE', [
          { controlId: 'dynamic-control', position: 0 },
        ]);
        this.addControlsForBlock(blockControlsMap, 'STRIPE', [
          { controlId: 'dynamic-control', position: 0 },
        ]);
      }

      if (enableBackup) {
        this.addControlsForBlock(blockControlsMap, 'CONTAINER', [
          { controlId: 'backup-control', position: 3 },
        ]);
      }


      this.addControlsForBlock(blockControlsMap, 'BLOCK_TEXT', [
        { controlId: 'text-transform-control', position: 2 },
      ]);

      this.addControlsForBlock(blockControlsMap, 'BLOCK_BUTTON', [
        { controlId: 'text-transform-control', position: 2 },
      ]);

      this.addControlsForBlock(blockControlsMap, 'BLOCK_IMAGE', [
        { controlId: 'custom-toolbar-button-control', position: 3 },
        { controlId: 'write-on-image-control', position: 4 },
      ]);
    }

    private addControlsForBlock(
      blockControlsMap: Record<string, SettingsPanelTab[]>,
      blockType: string,
      controls: { controlId: string; position: number }[],
    ) {
      const existingTabs = blockControlsMap[blockType];
      if (!existingTabs) return;

      let settingsTab = existingTabs.find(
        tab => tab.getTabId() === SettingsTab.SETTINGS
      );

      if (!settingsTab) {
        // If the SETTINGS tab doesn’t exist yet, create it with all controls in order
        settingsTab = new SettingsPanelTab(
          SettingsTab.SETTINGS,
          controls.sort((a, b) => a.position - b.position).map(c => c.controlId),
        );
        existingTabs.push(settingsTab);
        return;
      }

      // If the tab already exists, add the controls at the given positions
      for (const { controlId, position } of controls) {
        settingsTab.addControl(controlId, position);
      }
    }
  };
}

export class CustomSettingsRegistry extends createSettingsRegistryClass(true) { }

function isElementNode(node: ImmutableHtmlNode): node is ImmutableHtmlElementNode {
  return typeof (node as ImmutableHtmlElementNode).getOuterHTML === 'function';
}

/**
 * Vérifie si un ancêtre a deebr-data-dynamic="true"
 */
function checkParentDynamic(el: ImmutableHtmlElementNode): boolean {
  // On utilise closest() pour chercher un parent avec l'attribut.
  // Attention : closest() commence par l'élément lui-même. 
  // Si on veut exclure l'élément lui-même, il faut vérifier le parent du closest ou utiliser parent() d'abord.

  // Approche sécurisée : on remonte au parent immédiat, puis on cherche closest
  const parent = el.parent();
  if (!parent) return false;

  // Si le parent n'est pas un élément (ex: document root), on arrête
  // cast via 'any' ou check de type si possible
  const parentEl = (parent as any).asElement ? (parent as any).asElement() : null;
  if (!parentEl) return false;

  // On cherche sur le parent ou ses ancêtres
  const ancestor = parentEl.closest('[deebr-data-dynamic="true"]');
  return !!ancestor;
}


export class DynamicControl extends Control {
  // Global flag to control if locking logic should be active
  public static isDynamicEmail: boolean = false;
  // Default categories applied when user toggles dynamic ON for the first time
  public static defaultCategoryIds: number[] = [];
  public static defaultMetaCategoryId: number | undefined = undefined;
  private selectedNode: ImmutableHtmlNode | null = null;
  public toastService?: ToastService;

  override getId() {
    return "dynamic-control";
  }

  override getTemplate() {

    return `
    <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />


  <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; margin:20px 20px;" >

    <${UIElementType.LABEL}
      ${UEAttr.LABEL.text}="🪄 Personnaliser cet élément"
      style="font-size:14px;" >
  </${UIElementType.LABEL}>

  <${UIElementType.SWITCHER}
      ${UEAttr.SWITCHER.name}="dynamic" >
  </${UIElementType.SWITCHER}>

  </div>
    <${UIElementType.MESSAGE}
    name="parentDynamicAlert"
    type="info"
    style="margin-bottom: 5px;margin-left:20px;margin-right:20px" >
    </${UIElementType.MESSAGE}>
  <!-- Panneau dynamique = UI element, qu'on va cacher / montrer avec setVisibility -->
    <${UIElementType.NESTED_CONTROL}
        ${UEAttr.DEFAULT.name}="dynamicSettings"
        ${UEAttr.NESTED_CONTROL.controlId}="dynamic-settings-control" >
  </${UIElementType.NESTED_CONTROL}>
  <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />
    `;
  }

  override onRender(): void {
    // au départ : panneau masqué et alerte masquée
    this.api.setVisibility("dynamicSettings", false);
    this.api.setVisibility("parentDynamicAlert", false);

    // quand le switch change
    this.api.onValueChanged("dynamic", (newValue) => {

      const isOn = !!newValue;

      if (isOn) {
        // Si on active, on désactive les enfants potentiels
        this.cleanChildrenDynamic();
      }

      // montrer / cacher le panneau via l’API Stripo
      this.api.setVisibility("dynamicSettings", isOn);

      // appliquer deebr-data-dynamic sur le HTML
      this.applyDynamic(isOn);
    });
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;

    if (node.getType() !== 'element') return;
    const el = (node as any).asElement() as ImmutableHtmlElementNode;

    // 1. Check deeply (but limited) for product ID
    const hasProductId = hasAttributeInTree(el, ['deebr-product-id', 'deebr-promo-id', 'deebr-category-id']);

    if (!hasProductId) {
      this.api.updateValues({ dynamic: false });
      this.api.setUIEAttribute('dynamic', 'disabled', true);
      this.api.updateValues({ parentDynamicAlert: "Ce bloc n'est pas personnalisable" });
      this.api.setVisibility("parentDynamicAlert", true);
      this.api.setVisibility("dynamicSettings", false);
      // Force UNLOCK when clicking non-Deebr blocks
      this.updateDynamicPermissions(false);
      return;
    }

    // 2. Vérifier si un parent est DÉJÀ dynamique
    const parentIsDynamic = checkParentDynamic(el);
    // ... rest of method


    if (parentIsDynamic) {
      // Si parent dynamique : on force OFF, on disable le switch, on affiche l'alerte
      this.api.updateValues({ dynamic: false });
      this.api.setUIEAttribute('dynamic', 'disabled', true);

      this.api.updateValues({ parentDynamicAlert: "Cet élément fait déjà partie d'une structure dynamique." });
      this.api.setVisibility("parentDynamicAlert", true);
      this.api.setVisibility("dynamicSettings", false);

      // Force LOCK if parent is dynamic
      this.updateDynamicPermissions(true);

      // On s'assure que cet élément n'a PAS l'attribut (nettoyage au cas où)
      if (el.getAttribute("deebr-data-dynamic") === "true") {
        this.applyDynamic(false);
      }

    } else {
      // Parent NON dynamique : comportement normal
      this.api.setUIEAttribute('dynamic', 'disabled', false);
      this.api.setVisibility("parentDynamicAlert", false);

      const isDynamic = el.getAttribute("deebr-data-dynamic") === "true";

      // Toggle Permissions based on dynamic state (True = ReadOnly/Locked)
      this.updateDynamicPermissions(isDynamic);

      if (isDynamic) {
        // LOCKING: Force contenteditable="false" to prevent text editing
        if (el.getAttribute("contenteditable") !== "false") {
          const modifier = this.api.getDocumentModifier();
          const nodeModifier = modifier.modifyHtml(el);
          nodeModifier.setAttribute("contenteditable", "false");
          modifier.apply(new ModificationDescription("Lock dynamic block"));
        }

        // WARNING: Trigger generic warning toast ONLY if the email itself is dynamic
        if (DynamicControl.isDynamicEmail) {
          const now = Date.now();
          // Use a longer debounce or check if we just showed it
          const lastToast = (this as any)._lastToast || 0;
          if (now - lastToast > 2000) {
            (this as any)._lastToast = now;
            this.toastService?.error(
              'Modification impossible',
              'Ce bloc est généré dynamiquement et ne peut pas être modifié',
              true
            );
          }
        }
      } else {
        // UNLOCKING
        if (el.getAttribute("contenteditable") === "false") {
          const modifier = this.api.getDocumentModifier();
          const nodeModifier = modifier.modifyHtml(el);
          nodeModifier.removeAttribute("contenteditable");
          modifier.apply(new ModificationDescription("Unlock dynamic block"));
        }
      }

      // synchro du switch
      this.api.updateValues({ dynamic: isDynamic });

      // synchro de la visibilité du panneau (Dynamic Settings panel)
      this.api.setVisibility("dynamicSettings", isDynamic);
    }
  }

  /* PERMISSION-BASED LOCKING (Read-Only Mode) */
  private updateDynamicPermissions(readonly: boolean) {
    // SECURITY OVERRIDE: If the email itself is not dynamic, NEVER lock.
    if (!DynamicControl.isDynamicEmail) {
      // console.log('[Deebr] Email is NOT dynamic -> forcing unlock');
      readonly = false;
    }

    console.log(`[Deebr] updateDynamicPermissions: Readonly=${readonly} | Context: ${window.location.href}`);

    const hideElement = (el: HTMLElement, hide: boolean) => {
      // Use !important on inline styles to override framework styles
      if (hide) {
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      } else {
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
      }
    };

    const panelSelectors = [
      'ue-control-panel',
      '.control-panel',
      '.esd-settings-panel',
      '.esdev-settings-panel',
      '.control-panel-header-wrapper',
      '#stripoSettingsContainer'
    ];

    // Helper to search a specific document (and its Shadow DOMs)
    const scanDoc = (doc: Document, source: string) => {
      try {
        let foundAny = false;

        // Standard Query
        panelSelectors.forEach(selector => {
          const elements = doc.querySelectorAll(selector);
          if (elements.length > 0) {
            elements.forEach(el => hideElement(el as HTMLElement, readonly));
            foundAny = true;
          }
        });

        // Toggle Body Class
        if (readonly) doc.body.classList.add('deebr-stripo-locked');
        else doc.body.classList.remove('deebr-stripo-locked');

        // DEEP SEARCH: Pierce Shadow DOMs
        // Walk the tree to find shadow roots
        const walk = (node: Node) => {
          if (node.nodeType === 1) { // Element
            const el = node as HTMLElement;
            if (el.shadowRoot) {
              // Found a shadow root, search inside
              // console.log(`[Deebr] Found ShadowRoot on <${el.tagName}> in ${source}`);
              panelSelectors.forEach(selector => {
                const elements = el.shadowRoot!.querySelectorAll(selector);
                if (elements.length > 0) {
                  elements.forEach(match => hideElement(match as HTMLElement, readonly));
                  foundAny = true;
                }
              });
              // Recurse into this shadow root
              Array.from(el.shadowRoot.children).forEach(walk);
            }
          }
          // Recurse children
          let child = node.firstChild;
          while (child) {
            walk(child);
            child = child.nextSibling;
          }
        };

        walk(doc.body);

      } catch (e) {
        // Ignore cross-origin access errors
      }
    };

    // Recursive Frame Crawler + MutationObserver (Simplified for stability)
    const visited = new Set<Window>();

    // We attach a GLOBAL Observer to the main document body that scans DEEP on changes
    // This is expensive but necessary if we miss the initial render
    const obsKey = '__deebr_lock_observer_v2__';

    const traverseFrames = (win: Window, depth = 0) => {
      if (visited.has(win)) return;
      visited.add(win);
      if (depth > 5) return;

      try {
        const doc = win.document;
        const source = `Depth ${depth} - ${win.location.href}`;

        // 1. Immediate Scan
        scanDoc(doc, source);

        // 2. Setup Persistent Observer (Handling Async + Shadow)
        const existingObs = (win as any)[obsKey];

        if (readonly) {
          if (!existingObs) {
            // A simpler observer: matches ANY add, then throttled rescan
            // We don't try to parse mutations perfectly because ShadowDOM mutations are hard to track cheaply
            // We just trigger a rescan if nodes are added.
            let timeout: any;
            const observer = new MutationObserver((mutations) => {
              // Debounce the scan
              if (timeout) clearTimeout(timeout);
              timeout = setTimeout(() => {
                scanDoc(doc, `${source} (Async)`);
              }, 100);
            });

            observer.observe(doc.body, { childList: true, subtree: true });
            (win as any)[obsKey] = observer;
            console.log(`[Deebr] Deep Observer attached to ${source}`);
          }
        } else {
          if (existingObs) {
            existingObs.disconnect();
            delete (win as any)[obsKey];
            console.log(`[Deebr] Deep Observer removed from ${source}`);
          }
        }

        // Recurse frames
        for (let i = 0; i < win.frames.length; i++) {
          traverseFrames(win.frames[i], depth + 1);
        }
      } catch (e) {
        // Blocked
      }
    };

    // Start crawling
    try {
      const root = window.top || window.parent || window;
      // console.log('[Deebr] Starting search from root');
      traverseFrames(root);
    } catch (e) {
      // console.warn('[Deebr] Top/Parent access blocked, falling back to self', e);
      traverseFrames(window);
    }
  }

  /**
   * Désactive deebr-data-dynamic sur tous les enfants
   */
  private cleanChildrenDynamic(): void {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    // On cherche tous les descendants qui ont deebr-data-dynamic="true"
    const children = el.querySelectorAll('[deebr-data-dynamic="true"]');

    if (children && children.length > 0) {
      const modifier = this.api.getDocumentModifier();
      children.forEach((child: ImmutableHtmlElementNode) => {
        modifier.modifyHtml(child).removeAttribute("deebr-data-dynamic");
      });
      // On applique les modifs silencieusement ou groupées ?
      // Ici on l'ajoute à la modif courante si appelée depuis onRender (qui trigger applyDynamic après)
      // MAIS le applyDynamic est appelé juste après dans le callback.
      // Il vaut mieux appliquer tout de suite pour être propre.
      modifier.apply(new ModificationDescription("Disable dynamic on children"));
    }
  }

  private applyDynamic(isDynamic: boolean): void {
    if (!this.selectedNode) return;

    const anyNode: any = this.selectedNode;
    const el = anyNode.asElement?.();
    if (!el) return;

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (isDynamic) {
      nodeModifier.setAttribute("deebr-data-dynamic", "true");

      // Auto-apply email default categories if not already set on this element.
      // Done here (not in DynamicSettingsControl.onRender) to avoid a race when
      // the nested panel mounts before its onTemplateNodeUpdated fires — e.g.
      // when dynamic is toggled on a single container rather than the full block.
      const existingCats = el.getAttribute("deebr-data-categories");
      let hasCats = false;
      if (existingCats) {
        try {
          const parsed = JSON.parse(existingCats);
          hasCats = Array.isArray(parsed) && parsed.length > 0;
        } catch (e) { }
      }
      if (!hasCats && DynamicControl.defaultCategoryIds.length > 0) {
        nodeModifier.setAttribute(
          "deebr-data-categories",
          JSON.stringify(DynamicControl.defaultCategoryIds)
        );
        if (DynamicControl.defaultMetaCategoryId) {
          nodeModifier.setAttribute(
            "deebr-meta-category-id",
            DynamicControl.defaultMetaCategoryId.toString()
          );
        }
      }
    } else {
      nodeModifier.removeAttribute("deebr-data-dynamic");
      nodeModifier.removeAttribute("deebr-data-categories");
      nodeModifier.removeAttribute("deebr-meta-category-id");
      nodeModifier.removeAttribute("deebr-data-promo-filter");
      nodeModifier.removeAttribute("deebr-data-size-filter");
      nodeModifier.removeAttribute(MIN_SIZES_ATTR);

      // Nettoyer aussi les attributs catégorie sur tous les enfants
      const childrenWithCats = collectElementsWithAttribute(el, 'deebr-data-categories');
      for (const child of childrenWithCats) {
        const childMod = modifier.modifyHtml(child);
        childMod.removeAttribute("deebr-data-categories");
        childMod.removeAttribute("deebr-meta-category-id");
      }
      const childrenWithPromo = collectElementsWithAttribute(el, 'deebr-data-promo-filter');
      for (const child of childrenWithPromo) {
        modifier.modifyHtml(child).removeAttribute("deebr-data-promo-filter");
      }
      const childrenWithSize = collectElementsWithAttribute(el, 'deebr-data-size-filter');
      for (const child of childrenWithSize) {
        modifier.modifyHtml(child).removeAttribute("deebr-data-size-filter");
      }
      const childrenWithMinSizes = collectElementsWithAttribute(el, MIN_SIZES_ATTR);
      for (const child of childrenWithMinSizes) {
        modifier.modifyHtml(child).removeAttribute(MIN_SIZES_ATTR);
      }
    }

    modifier.apply(
      new ModificationDescription(
        isDynamic ? "Enable dynamic block" : "Disable dynamic block"
      )
    );
  }
}

/**
 * Filtre « Tailles en stock minimum » d'un bloc personnalisé : deebr-data-min-sizes = entier
 * (absent = 0 = pas de filtre). Lu par tasks_dynamic.py sur le parent dynamique et appliqué
 * au pool de produits (produit_sales.n_sizes), comme le filtre promo.
 */
export const MIN_SIZES_ATTR = 'deebr-data-min-sizes';
const MIN_SIZES_MAX = 30;

/** Valeur du compteur (number ou string selon Stripo) → entier borné [0, MIN_SIZES_MAX]. */
export function parseMinSizes(value: unknown): number {
  const n = parseInt(String(value ?? '').trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, MIN_SIZES_MAX);
}

export class DynamicSettingsControl extends Control {
  public currentCategoryNames: string = "";
  public currentCategoryIds: number[] = [];
  public currentMetaCategoryId: number | undefined = undefined;
  public openCategoriesModal!: (currentIds?: number[], currentMetaId?: number) => Promise<{ metaCategoryId: number, categoryIds: number[] }>;
  public resolveCategoryNames!: (ids: number[]) => string;
  // Enterprise.has_size_data : le client a une grille de tailles (Eram) -> affiche
  // le filtre "Pointure du destinataire" (deebr-data-size-filter = off | band | strict).
  public hasSizeData: boolean = false;
  // Enterprise.has_size_stock : produit_sales porte n_sizes (tailles en stock) -> affiche
  // le compteur "Tailles en stock minimum" (deebr-data-min-sizes).
  public hasSizeStock: boolean = false;
  private selectedNode: ImmutableHtmlNode | null = null;

  override getId() {
    return "dynamic-settings-control";
  }

  override getTemplate() {
    return `
      <div style="margin:20px 20px;">
        <${UIElementType.MESSAGE}
          ${UEAttr.DEFAULT.name}="selectionSummary"
          type="info"
          style="margin-bottom: 10px;">
        </${UIElementType.MESSAGE}>

        <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="Modifier la sélection"
          ${UEAttr.BUTTON.name}="modifySelection">
        </${UIElementType.BUTTON}>

        <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Produits personnalisés à afficher :"
          style="margin-top: 16px; margin-bottom: 6px; display: block; font-size:13px;">
        </${UIElementType.LABEL}>

        <${UIElementType.RADIO_BUTTONS}
          ${UEAttr.RADIO_BUTTONS.name}="promoFilter">

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Tous"
            ${UEAttr.RADIO_ITEM.value}="all">
          </${UIElementType.RADIO_ITEM}>

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Promo"
            ${UEAttr.RADIO_ITEM.value}="promo">
          </${UIElementType.RADIO_ITEM}>

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Hors promo"
            ${UEAttr.RADIO_ITEM.value}="no_promo">
          </${UIElementType.RADIO_ITEM}>

        </${UIElementType.RADIO_BUTTONS}>

        <${UIElementType.LABEL}
          ${UEAttr.LABEL.name}="sizeFilterLabel"
          ${UEAttr.LABEL.text}="Pointure du destinataire :"
          ${UEAttr.LABEL.hint}="Chaque destinataire ne voit que des produits disponibles dans sa pointure (déduite de ses achats). Si sa pointure est inconnue, comportement habituel."
          style="margin-top: 16px; margin-bottom: 6px; display: block; font-size:13px;">
        </${UIElementType.LABEL}>

        <${UIElementType.RADIO_BUTTONS}
          ${UEAttr.RADIO_BUTTONS.name}="sizeFilter">

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Ignorer"
            ${UEAttr.RADIO_ITEM.value}="off">
          </${UIElementType.RADIO_ITEM}>

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Adapter (±1)"
            ${UEAttr.RADIO_ITEM.hint}="Pointure habituelle et les tailles voisines"
            ${UEAttr.RADIO_ITEM.value}="band">
          </${UIElementType.RADIO_ITEM}>

          <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Strict"
            ${UEAttr.RADIO_ITEM.hint}="Uniquement les pointures déjà achetées"
            ${UEAttr.RADIO_ITEM.value}="strict">
          </${UIElementType.RADIO_ITEM}>

        </${UIElementType.RADIO_BUTTONS}>

        <${UIElementType.LABEL}
          ${UEAttr.LABEL.name}="minSizesLabel"
          ${UEAttr.LABEL.text}="Tailles en stock minimum :"
          ${UEAttr.LABEL.hint}="N'affiche que les produits ayant au moins ce nombre de tailles en stock (0 = pas de filtre). Un produit à taille unique compte 1."
          style="margin-top: 16px; margin-bottom: 6px; display: block; font-size:13px;">
        </${UIElementType.LABEL}>

        <${UIElementType.COUNTER}
          ${UEAttr.COUNTER.name}="minSizes"
          ${UEAttr.COUNTER.minValue}="0"
          ${UEAttr.COUNTER.maxValue}="${MIN_SIZES_MAX}"
          ${UEAttr.COUNTER.step}="1">
        </${UIElementType.COUNTER}>
      </div>
    `;
  }

  override onRender() {
    this.api.onValueChanged('modifySelection', async () => {
      if (!this.openCategoriesModal) return;

      let currentIds: number[] = [];
      let currentMetaId: number | undefined;
      const el = (this.selectedNode as any)?.asElement?.();
      const saved = el?.getAttribute('deebr-data-categories');
      const savedMeta = el?.getAttribute('deebr-meta-category-id');
      if (saved) {
        try { currentIds = JSON.parse(saved); } catch (e) { }
      }
      if (savedMeta) {
        currentMetaId = parseInt(savedMeta, 10);
      }

      const result = await this.openCategoriesModal(currentIds, currentMetaId);
      if (result && result.categoryIds) {
        this.saveSelectedCategories(result.categoryIds, result.metaCategoryId);
        this.updateSelectionSummary(result.categoryIds);
      }
    });

    this.api.onValueChanged('promoFilter', (newValue) => {
      const v = (newValue as string) || 'all';
      this.saveSelectedPromoFilter(v);
    });

    this.api.onValueChanged('sizeFilter', (newValue) => {
      const v = (newValue as string) || 'off';
      this.saveSelectedSizeFilter(v);
    });

    this.api.onValueChanged('minSizes', (newValue) => {
      this.saveMinSizes(parseMinSizes(newValue));
    });

    this.applySizeFilterVisibility();

    setTimeout(() => {
      try {
        // Read-only: update the summary from whatever is currently on the DOM.
        // Writing defaults is handled by DynamicControl.applyDynamic (toggle ON)
        // and by onTemplateNodeUpdated below (legacy dynamic elements without categories).
        const el = (this.selectedNode as any)?.asElement?.();
        let ids: number[] = [];
        if (el) {
          const saved = el.getAttribute('deebr-data-categories');
          if (saved) {
            try { ids = JSON.parse(saved); } catch (e) { }
          }
          const promo = el.getAttribute('deebr-data-promo-filter') || 'all';
          this.api.updateValues({ promoFilter: promo });
          const sizeFilter = el.getAttribute('deebr-data-size-filter') || 'off';
          this.api.updateValues({ sizeFilter });
          this.api.updateValues({ minSizes: parseMinSizes(el.getAttribute(MIN_SIZES_ATTR)) });
        }
        this.updateSelectionSummary(ids);
        this.applySizeFilterVisibility();
      } catch (e) {
        console.warn('[Deebr] Failed to update selection summary in onRender', e);
      }
    }, 500);
  }

  /** Le filtre pointure n'a de sens que si le client a une grille de tailles (Enterprise.has_size_data). */
  private applySizeFilterVisibility() {
    try {
      this.api.setVisibility('sizeFilterLabel', this.hasSizeData);
      this.api.setVisibility('sizeFilter', this.hasSizeData);
      this.api.setVisibility('minSizesLabel', this.hasSizeStock);
      this.api.setVisibility('minSizes', this.hasSizeStock);
    } catch (e) { }
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;
    const el = (node as any).asElement?.();

    if (el) {
      let ids: number[] = [];
      const savedRaw = el.getAttribute('deebr-data-categories');
      if (savedRaw) {
        try {
          const saved = JSON.parse(savedRaw);
          if (Array.isArray(saved) && saved.length > 0) {
            ids = saved;
          }
        } catch (e) { }
      }

      if (ids.length === 0) {
        const isDynamic = el.getAttribute('deebr-data-dynamic') === 'true';
        // Sans catégories par défaut (email importé, sans meta-catégorie), il n'y a rien à
        // écrire : appeler saveSelectedCategories([]) faisait un modifier.apply() à vide,
        // Stripo re-déclenchait onTemplateNodeUpdated → boucle infinie + autosave en continu.
        if (isDynamic && this.currentCategoryIds.length > 0) {
          this.saveSelectedCategories(this.currentCategoryIds, this.currentMetaCategoryId);
          ids = this.currentCategoryIds;
        }
      }

      this.updateSelectionSummary(ids);

      const promo = el.getAttribute('deebr-data-promo-filter') || 'all';
      try { this.api.updateValues({ promoFilter: promo }); } catch (e) { }
      const sizeFilter = el.getAttribute('deebr-data-size-filter') || 'off';
      try { this.api.updateValues({ sizeFilter }); } catch (e) { }
      try { this.api.updateValues({ minSizes: parseMinSizes(el.getAttribute(MIN_SIZES_ATTR)) }); } catch (e) { }
      this.applySizeFilterVisibility();
    }
  }

  private saveSelectedCategories(ids: number[], metaId?: number | null) {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    // Ne jamais faire un modifier.apply() sans changement réel : Stripo re-déclenche
    // onTemplateNodeUpdated après chaque apply, ce qui bouclerait à l'infini.
    const hasIds = !!ids && ids.length > 0;
    const targetCats = hasIds ? JSON.stringify(ids) : null;
    const currentCats = el.getAttribute('deebr-data-categories');
    const currentMeta = el.getAttribute('deebr-meta-category-id');
    const targetMeta = hasIds ? (metaId ? metaId.toString() : currentMeta) : null;
    const childrenWithCats = collectElementsWithAttribute(el, 'deebr-data-categories');
    if (currentCats === targetCats && currentMeta === targetMeta && childrenWithCats.length === 0) {
      return;
    }

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (hasIds) {
      nodeModifier.setAttribute('deebr-data-categories', targetCats!);
      if (metaId) {
        nodeModifier.setAttribute('deebr-meta-category-id', metaId.toString());
      }
    } else {
      nodeModifier.removeAttribute('deebr-data-categories');
      nodeModifier.removeAttribute('deebr-meta-category-id');
    }

    // Toujours nettoyer les enfants — le backend ne doit lire ces attributs que sur le parent
    for (const child of childrenWithCats) {
      const childMod = modifier.modifyHtml(child);
      childMod.removeAttribute('deebr-data-categories');
      childMod.removeAttribute('deebr-meta-category-id');
    }

    modifier.apply(new ModificationDescription('Updated personalization categories'));
  }

  private saveSelectedPromoFilter(value: string) {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    const current = el.getAttribute('deebr-data-promo-filter') || 'all';
    if (current === value) return;

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (value && value !== 'all') {
      nodeModifier.setAttribute('deebr-data-promo-filter', value);
    } else {
      nodeModifier.removeAttribute('deebr-data-promo-filter');
    }

    // Comme pour deebr-data-categories : le backend ne lit cet attribut que sur le parent dynamique
    const childrenWithPromo = collectElementsWithAttribute(el, 'deebr-data-promo-filter');
    for (const child of childrenWithPromo) {
      modifier.modifyHtml(child).removeAttribute('deebr-data-promo-filter');
    }

    modifier.apply(new ModificationDescription('Updated promo filter'));
  }

  /**
   * Filtre pointure du destinataire : deebr-data-size-filter = band | strict (absent = off).
   * Lu par tasks_dynamic.py sur le parent dynamique, applique par destinataire a l'affectation.
   */
  private saveSelectedSizeFilter(value: string) {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    const current = el.getAttribute('deebr-data-size-filter') || 'off';
    if (current === value) return;

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (value && value !== 'off') {
      nodeModifier.setAttribute('deebr-data-size-filter', value);
    } else {
      nodeModifier.removeAttribute('deebr-data-size-filter');
    }

    const childrenWithSize = collectElementsWithAttribute(el, 'deebr-data-size-filter');
    for (const child of childrenWithSize) {
      modifier.modifyHtml(child).removeAttribute('deebr-data-size-filter');
    }

    modifier.apply(new ModificationDescription('Updated size filter'));
  }

  /**
   * Filtre « Tailles en stock minimum » : deebr-data-min-sizes = entier > 0 (absent = 0 = off).
   * Lu par tasks_dynamic.py sur le parent dynamique, applique au pool de produits du bloc.
   */
  private saveMinSizes(value: number) {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    const current = parseMinSizes(el.getAttribute(MIN_SIZES_ATTR));
    // Jamais de modifier.apply() sans changement reel (sinon boucle onTemplateNodeUpdated)
    if (current === value) return;

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (value > 0) {
      nodeModifier.setAttribute(MIN_SIZES_ATTR, String(value));
    } else {
      nodeModifier.removeAttribute(MIN_SIZES_ATTR);
    }

    const childrenWithMinSizes = collectElementsWithAttribute(el, MIN_SIZES_ATTR);
    for (const child of childrenWithMinSizes) {
      modifier.modifyHtml(child).removeAttribute(MIN_SIZES_ATTR);
    }

    modifier.apply(new ModificationDescription('Updated min sizes filter'));
  }

  private updateSelectionSummary(ids: number[]) {
    if (!this.api) return;

    let text: string;
    if (ids.length > 0 && this.resolveCategoryNames) {
      const names = this.resolveCategoryNames(ids) || `${ids.length} catégorie(s)`;
      text = `Bloc personnalisé sur : ${names}`;
    } else if (ids.length > 0) {
      text = `Bloc personnalisé sur : ${ids.length} catégorie(s)`;
    } else {
      text = 'Aucune catégorie sélectionnée';
    }

    try {
      this.api.updateValues({ 'selectionSummary': text });
      this.api.setVisibility('selectionSummary', true);
      this.api.setVisibility('modifySelection', true);
    } catch (e) { }
  }
}


/**
 * Helper to check for attribute with limited depth to avoid matching too deep in the tree
 */
function collectElementsWithAttribute(el: ImmutableHtmlElementNode, attr: string, result: ImmutableHtmlElementNode[] = []): ImmutableHtmlElementNode[] {
  const children = el.children();
  if (!children || children.length === 0) return result;
  for (const child of children) {
    if (child.getType() === 'element') {
      const childEl = child.asElement() as ImmutableHtmlElementNode;
      if (childEl.getAttribute(attr) !== null) {
        result.push(childEl);
      }
      collectElementsWithAttribute(childEl, attr, result);
    }
  }
  return result;
}

function hasAttributeInTree(el: ImmutableHtmlElementNode, attrs: string[], maxDepth: number = Infinity): boolean {
  // Check self
  for (const attr of attrs) {
    if (el.getAttribute(attr) !== null) return true;
  }

  if (maxDepth <= 0) return false;

  // Check children
  const children = el.children();
  if (!children || children.length === 0) return false;

  for (const child of children) {
    if (child.getType() === 'element') {
      const childEl = child.asElement() as ImmutableHtmlElementNode;
      if (hasAttributeInTree(childEl, attrs, maxDepth - 1)) return true;
    }
  }
  return false;
}

export class BackupControl extends Control {
  public openBackupSelectionModal!: (ids?: string[]) => Promise<string[]>;
  private selectedNode: ImmutableHtmlNode | null = null;

  override getId() {
    return "backup-control";
  }

  override getTemplate() {
    return `
  <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; margin:6px 20px;" >
    <${UIElementType.LABEL}
            ${UEAttr.LABEL.text}="Backup"
            style="font-size:14px;" >
  </${UIElementType.LABEL}>

  <${UIElementType.SWITCHER}
            ${UEAttr.SWITCHER.name}="backupWithFallback" >
  </${UIElementType.SWITCHER}>
  </div>

  <div style="margin-top: 5px;" >
    <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="Choisir mes backup"
          ${UEAttr.BUTTON.name}="chooseBackup">
  </${UIElementType.BUTTON}>
  </div>
    `;
  }

  override onRender(): void {
    // Initialement, on cache le bouton si le switch n'est pas activé
    this.api.setVisibility("chooseBackup", false);

    this.api.onValueChanged("backupWithFallback", (val) => {
      const isOn = !!val;
      this.api.setVisibility("chooseBackup", isOn);
      if (!isOn) {
        // If turned off, remove the backups attribute?
        this.saveSelectedBackups([]);
      }
    });

    this.api.onValueChanged("chooseBackup", async () => {
      if (this.openBackupSelectionModal) {
        let currentIds: string[] = [];
        const el = (this.selectedNode as any)?.asElement?.();
        const saved = el?.getAttribute('deebr-data-backups');
        if (saved) {
          try { currentIds = JSON.parse(saved); } catch (e) { }
        }

        const selectedIds = await this.openBackupSelectionModal(currentIds);
        this.saveSelectedBackups(selectedIds);
      }
    });
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;
    const el = (node as any).asElement?.();
    if (!el) return;

    // Conditions d'activation :
    // 1. L'élément est dynamique
    const isDynamic = el.getAttribute("deebr-data-dynamic") === "true";
    // 2. OU un de ses parents est dynamique
    const parentIsDynamic = checkParentDynamic(el);

    const isEnabled = isDynamic || parentIsDynamic;

    if (isEnabled) {
      this.api.setUIEAttribute('backupWithFallback', 'disabled', false);
      const hasBackups = el.getAttribute('deebr-data-backups');
      if (hasBackups) {
        this.api.updateValues({ backupWithFallback: true });
        this.api.setVisibility("chooseBackup", true);
      } else {
        // If enabled but no backups, default off? 
        // Or check if user previously set it?
        // For now, if no attribute, it's off.
        this.api.updateValues({ backupWithFallback: false });
        this.api.setVisibility("chooseBackup", false);
      }
    } else {
      // Si non éligible, on désactive le switch et on le force à OFF
      this.api.setUIEAttribute('backupWithFallback', 'disabled', true);
      this.api.updateValues({ backupWithFallback: false });
      this.api.setVisibility("chooseBackup", false);
    }
  }

  private saveSelectedBackups(ids: string[]) {
    if (!this.selectedNode) return;
    const el = (this.selectedNode as any).asElement?.();
    if (!el) return;

    const modifier = this.api.getDocumentModifier();
    const nodeModifier = modifier.modifyHtml(el);

    if (ids && ids.length > 0) {
      nodeModifier.setAttribute('deebr-data-backups', JSON.stringify(ids));
    } else {
      nodeModifier.removeAttribute('deebr-data-backups');
    }

    modifier.apply(new ModificationDescription('Updated backup selection'));
  }
}






type TextTransform = 'lowercase' | 'uppercase' | 'capitalize';
type TextTransformOption = TextTransform | null;

export class TextTransformControl extends Control {
  private selectedNode: ImmutableHtmlNode | null = null;
  private readonly TEXT_TAGS: Set<string> = new Set([
    'p',
    'span',
    'a',
    'strong',
    'em',
    'b',
    'i',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6'
  ]);

  override getId() {
    return 'text-transform-control';
  }

  override getTemplate() {
    return `
  <div style="padding: 10px 20px;" >
    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Changement de casse"
          style="margin-bottom: 6px; display: block;" >
  </${UIElementType.LABEL}>

  <${UIElementType.RADIO_BUTTONS}
          ${UEAttr.RADIO_BUTTONS.name}="textTransformValue" >

  <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Aucune"
            ${UEAttr.RADIO_ITEM.value}="none" >
  </${UIElementType.RADIO_ITEM}>

  <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="aa"
            ${UEAttr.RADIO_ITEM.value}="lowercase" >
  </${UIElementType.RADIO_ITEM}>

  <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="AA"
            ${UEAttr.RADIO_ITEM.value}="uppercase" >
  </${UIElementType.RADIO_ITEM}>

  <${UIElementType.RADIO_ITEM}
            ${UEAttr.RADIO_ITEM.text}="Aa"
            ${UEAttr.RADIO_ITEM.value}="capitalize" >
  </${UIElementType.RADIO_ITEM}>

  </${UIElementType.RADIO_BUTTONS}>
  </div>
    `;
  }


  override onRender(): void {
    this.api.onValueChanged("textTransformValue", (newValue) => {
      if (newValue === "none") this.applyTransform(null);
      else this.applyTransform(newValue);
    });
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;
  }

  private applyTransform(transform: TextTransformOption): void {
    if (!this.selectedNode) return;

    const nodes = this.getAllTextNodes(this.selectedNode);
    if (nodes.length === 0) return;

    const modifier = this.api.getDocumentModifier();

    // Queue all modifications
    nodes.forEach((n) => {
      const nodeModifier = modifier.modifyHtml(n);

      if (transform) {
        nodeModifier.setStyle("text-transform", transform);
      } else {
        nodeModifier.removeStyle("text-transform");
      }
    });

    // Apply everything in ONE atomic operation
    const descriptionText = transform
      ? `Text transform set to ${transform} on ${nodes.length} node(s)`
      : `Text transform removed from ${nodes.length} node(s)`;

    modifier.apply(new ModificationDescription(descriptionText));
  }

  private getAllTextNodes(node: ImmutableHtmlNode): ImmutableHtmlElementNode[] {
    const result: ImmutableHtmlElementNode[] = [];

    const dfs = (n: ImmutableHtmlNode) => {
      if (n.getType() !== "element") return;

      const el = n.asElement();
      const tagName = el.getTagName().toLowerCase(); // or equivalent

      // Only consider "text" tags
      if (this.TEXT_TAGS.has(tagName)) {
        const text = el.getInnerText()?.trim();
        if (text && text.length > 0) {
          result.push(el);
        }
      }

      // Still traverse children to find deeper text elements
      const children = el.children() || [];
      for (const child of children) {
        dfs(child);
      }
    };

    dfs(node);
    return result;
  }
}

export class CustomToolbarButtonControl extends Control {
  private selectedNode: ImmutableHtmlNode | null = null;
  public generatorService!: GeneratorService;
  public imageSelectionService!: ImageSelectionService;
  public dialogService!: NbDialogService;
  public dialogErrorAnimate!: TemplateRef<any>;
  public errorMessage = '';
  dialogRef: NbDialogRef<any> | undefined = undefined;
  private lastBlockId: string | null = null;
  public emailId!: string;
  public userId!: number;
  public userName!: string;
  public enterpriseId!: number;
  public uploadToStripoDelegate!: (url: string) => Promise<string>;
  public toastService!: ToastService

  constructor() {
    super();
  }

  override getId() {
    return 'custom-toolbar-button-control';
  }

  /** Returns normalized "#RRGGBB" or null (invalid/empty). Trims and removes internal spaces. */
  private _sanitizeHex(input?: unknown): string | null {
    if (input == null) return null;
    let s = String(input).trim().replace(/\s+/g, '');
    if (!s) return null;
    if (s[0] !== '#') s = '#' + s;
    const body = s.slice(1);

    // #RGB -> expand to #RRGGBB
    if (/^[0-9a-fA-F]{3}$/.test(body)) {
      const expanded = body.split('').map(ch => ch + ch).join('');
      return `#${expanded.toUpperCase()} `;
    }
    // #RRGGBB
    if (/^[0-9a-fA-F]{6}$/.test(body)) {
      return `#${body.toUpperCase()} `;
    }
    return null;
  }

  override getTemplate() {
    return `
    <hr style="border: none; border-top: 1px solid #fff; margin: 10px 0;">
    <div style="padding: 10px 20px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;" >

    <${UIElementType.BUTTON}
        ${UEAttr.BUTTON.caption}="🖼️ Ajouter logo sur l'image"
        ${UEAttr.BUTTON.name}="addLogoButton"
        style="margin-bottom: 5px; margin-right: 5px;" >
    </${UIElementType.BUTTON}>
    <${UIElementType.MESSAGE}
        name="customLogoAlert"
        type="info"
        style="margin-top: 5px;" >
    </${UIElementType.MESSAGE}>

    <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />
    <div style="flex-basis: 100%; height: 0;" > </div>

    <!-- Label -->
    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Choisissez la couleur du texte : (optionnel)"
          style="margin-right: 4px;" >
    </${UIElementType.LABEL}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <!-- Palette color picker -->
    <${UIElementType.COLOR}
          ${UEAttr.DEFAULT.name}="ModelTextColorPicker" >
    </${UIElementType.COLOR}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Texte personnalisé du modèle : (optionnel)"
          style="margin-right: 4px;" >
    </${UIElementType.LABEL}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.TEXT}
          ${UEAttr.TEXT.name}="ModelTextInput"
          ${UEAttr.TEXT.placeholder}="Entrez le texte personnalisé du modèle..." >
    </${UIElementType.TEXT}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Taille de la police : (optionnel)"
          style="margin-right: 4px;" >
    </${UIElementType.LABEL}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.TEXT}
          ${UEAttr.TEXT.name}="ModelFontSizeInput"
          ${UEAttr.TEXT.placeholder}="ex: 24" >
    </${UIElementType.TEXT}>

    <${UIElementType.BUTTON}
        ${UEAttr.BUTTON.caption}="📄 Ajouter modèle produit sur l'image"
        ${UEAttr.BUTTON.name}="addModelButton"
        style="margin-bottom: 5px; margin-right: 5px;" >
    </${UIElementType.BUTTON}>
    <${UIElementType.MESSAGE}
        name="customModelAlert"
        type="info"
        style="margin-top: 5px;" >
    </${UIElementType.MESSAGE}>

    <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />
    <div style="flex-basis: 100%; height: 0;" > </div>

    <!-- Label -->
    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Choisissez la couleur de la remise : (optionnel)"
          style="margin-right: 4px;" >
    </${UIElementType.LABEL}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <!-- Palette color picker -->
    <${UIElementType.COLOR}
          ${UEAttr.DEFAULT.name}="remiseColorPicker" >
    </${UIElementType.COLOR}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Texte personnalisé de la remise : (optionnel)"
          style="margin-right: 4px;" >
    </${UIElementType.LABEL}>

    <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.TEXT}
          ${UEAttr.TEXT.name}="remiseTextInput"
          ${UEAttr.TEXT.placeholder}="ex: -30€, SOLDES, -50% sur le 2e..." >
    </${UIElementType.TEXT}>

  <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="🔖% Mettre à jour remise"
          ${UEAttr.BUTTON.name}="customButton"
          style="margin-bottom: 5px; margin-right: 5px;" >
  </${UIElementType.BUTTON}>
  <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />
  <${UIElementType.MESSAGE}
      name="customRemiseAlert"
      type="info"
      style="margin-top: 5px;" >
  </${UIElementType.MESSAGE}>

  <${UIElementType.LABEL}
          ${UEAttr.LABEL.text}="Choisissez la couleur de fond de l'image : (optionnel)"
          style="margin-right: 4px;" >
  </${UIElementType.LABEL}>

  <div style="flex-basis: 100%; height: 0;" > </div>

    <!-- Palette color picker -->
      <${UIElementType.COLOR}
          ${UEAttr.DEFAULT.name}="removeBackgroundPicker" >
  </${UIElementType.COLOR}>
  <${UIElementType.BUTTON}
        ${UEAttr.BUTTON.caption}="✂️ Détourage image"
        ${UEAttr.BUTTON.name}="removeBackground"
        style="margin-bottom: 5px;" >
  </${UIElementType.BUTTON}>

  <${UIElementType.MESSAGE}
name = "RemoveBAckgroundAlert"
type = "info"
style = "margin-top: 5px;" >
  </${UIElementType.MESSAGE}>

  <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />
    <div style="flex-basis: 100%; height: 0;" > </div>

      <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="✨ Redimensionner IA"
          ${UEAttr.BUTTON.name}="resizeImageButton"
          style="margin-bottom: 5px;" >
  </${UIElementType.BUTTON}>

  <${UIElementType.BUTTON}
        ${UEAttr.BUTTON.caption}="✨ Redimensionnement rapide"
        ${UEAttr.BUTTON.name}="quickResize"
        style="margin-bottom: 5px;" >
  </${UIElementType.BUTTON}>
  <${UIElementType.MESSAGE}
      name="customResizeAlert"
      type="info"
      style="margin-top: 5px;" >
  </${UIElementType.MESSAGE}>
  <div style="flex-basis: 100%; height: 0;" > </div>

    <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="🎞️ Animation IA"
          ${UEAttr.BUTTON.name}="imageToGifButton"
          style="margin-bottom: 5px;" >
  </${UIElementType.BUTTON}>

    <${UIElementType.BUTTON}
          ${UEAttr.BUTTON.caption}="🎨 Ambiance IA"
          ${UEAttr.BUTTON.name}="ambianceButton"
          style="margin-bottom: 5px;" >
  </${UIElementType.BUTTON}>
  <hr style="border: none; border-top: 1px solid #ffffff; width: 100%;" />

    
  </div>
    `;
  }

  override onRender() {
    this.api.onValueChanged('customButton', (v) => { if (!v) return; this._majRemiseFromSelectedNode(); });
    this.api.onValueChanged('addLogoButton', (v) => { if (!v) return; this._addLogoFromSelectedNode(); });
    this.api.onValueChanged('resizeImageButton', (v) => { if (!v) return; this._onResizeImageButtonClickFromSelectedNode(); });
    this.api.onValueChanged('quickResize', (v) => { if (!v) return; this._onquickResizeButtonClickFromSelectedNode(); });
    this.api.onValueChanged('imageToGifButton', (v) => { if (!v) return; this._onImageToGifButtonClickFromSelectedNode(); });
    this.api.onValueChanged('ambianceButton', (v) => { if (!v) return; this._onAmbianceButtonClickFromSelectedNode(); });
    this.api.onValueChanged('removeBackground', (v) => { if (!v) return; this._onremoveBackgroundnClickFromSelectedNode(); });
    this.api.onValueChanged('addModelButton', (v) => { if (!v) return; this._addModelFromSelectedNode(); });
    this.api.updateValues({
      customModelAlert: '',
      customRemiseAlert: '',
      customLogoAlert: '',
      customResizeAlert: '',
      RemoveBAckgroundAlert: '',
    });
    this._hideEmptyAlerts();
  }

  // Stripo reconstruit le panneau sans repasser par onRender (re-sélection de bloc,
  // rebuild après modification) : les MESSAGE reviennent visibles et vides (encarts bleus).
  // On ré-applique donc le masquage à chaque onTemplateNodeUpdated, uniquement sur les
  // alertes vides pour ne pas couper une alerte en cours d'affichage.
  private _hideEmptyAlerts(): void {
    const alerts = ['customModelAlert', 'customRemiseAlert', 'customLogoAlert',
                    'customResizeAlert', 'RemoveBAckgroundAlert'];
    const values = this.api.getValues();
    for (const name of alerts) {
      if (!values[name]) {
        this.api.setVisibility(name, false);
      }
    }
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;
    this._hideEmptyAlerts();
  }


  private _majRemiseFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;
    const outerHTML = this.selectedNode.getOuterHTML();
    const jElement = $(outerHTML); // jQuery clone, manipulable
    this.onmajRemiseProduct();
  }

  private _onResizeImageButtonClickFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const outerHTML = this.selectedNode.getOuterHTML();
    const jElement = $(outerHTML); // clone manipulable
    this.onResizeImageButtonClick(jElement);
  }

  private _onquickResizeButtonClickFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const outerHTML = this.selectedNode.getOuterHTML();
    this.onRecadrageProduit();
  }

  private _onremoveBackgroundnClickFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const outerHTML = this.selectedNode.getOuterHTML();
    this.onRemoveBAckgroud();
  }

  private _onImageToGifButtonClickFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const outerHTML = this.selectedNode.getOuterHTML();
    const jElement = $(outerHTML); // clone manipulable
    this.onImageToGifButtonClick(jElement);
  }

  private _onAmbianceButtonClickFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const jElement = $(this.selectedNode.getOuterHTML()); // clone manipulable
    this.onAmbianceButtonClick(jElement);
  }

  private _restoreCustomButton(): void {
    this.api.setUIEAttribute('customButton', UEAttr.BUTTON.caption, '% Mettre à jour remise');
    this.api.setUIEAttribute('customButton', UEAttr.BUTTON.disabled, false);
  }

  private _addLogoFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;
    this.onAddLogoOnImage();
  }

  private _showAlertLogo(message: string, type: 'info' | 'warning' | 'error' | 'success') {
    this.api.updateValues({ customLogoAlert: message });
    this.api.setUIEAttribute('customLogoAlert', 'type', type);
    this.api.setVisibility('customLogoAlert', true);
    setTimeout(() => {
      this.api.setVisibility('customLogoAlert', false);
      this.api.updateValues({ customLogoAlert: '' });
    }, 4000);
  }

  private _restoreLogoButton(): void {
    this.api.setUIEAttribute('addLogoButton', UEAttr.BUTTON.caption, "🖼️ Ajouter logo sur l'image");
    this.api.setUIEAttribute('addLogoButton', UEAttr.BUTTON.disabled, false);
  }

  private _addModelFromSelectedNode(): void {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;
    this.onAddModelProduct();
  }

  private _restoreModelButton(): void {
    this.api.setUIEAttribute('addModelButton', UEAttr.BUTTON.caption, '📄 Ajouter modèle produit sur l\'image');
    this.api.setUIEAttribute('addModelButton', UEAttr.BUTTON.disabled, false);
  }

  private _showAlertModel(message: string, type: 'info' | 'warning' | 'error' | 'success') {
    this.api.updateValues({ customModelAlert: message });
    this.api.setUIEAttribute('customModelAlert', 'type', type);
    this.api.setVisibility('customModelAlert', true);
    setTimeout(() => {
      this.api.setVisibility('customModelAlert', false);
      this.api.updateValues({ customModelAlert: '' });
    }, 4000);
  }

  private async onAddModelProduct(): Promise<void> {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const imgNode = this.selectedNode.querySelector?.('img');
    if (!imgNode || !isElementNode(imgNode)) {
      console.warn('Image manquante ou invalide');
      return;
    }

    const imageUrl = imgNode.getAttribute('src') || '';
    const width = imgNode.getAttribute('width') || '100%';
    if (!imageUrl) {
      this._showAlertModel('❌ Aucune image trouvée dans le bloc', 'error');
      return;
    }

    const values = (this as any).api?.getValues?.() ?? {};
    const rawColor = (values['ModelTextColorPicker'] ?? '') as string;
    const rawText = (values['ModelTextInput'] ?? '') as string;
    const rawFontSize = (values['ModelFontSizeInput'] ?? '') as string;

    const color = this._sanitizeHex(rawColor) ?? undefined;
    const altText = imgNode.getAttribute('alt') || '';
    const text = rawText.trim() || altText.trim();
    const fontSize = parseInt(rawFontSize.trim(), 10) || undefined;

    this.api.setUIEAttribute('addModelButton', UEAttr.BUTTON.caption, '⏳ Ajout du modèle...');
    this.api.setUIEAttribute('addModelButton', UEAttr.BUTTON.disabled, true);

    try {
      const newImageUrl: string = await lastValueFrom(
        this.generatorService.addModelProduct(imageUrl, text, width, color, undefined, this.enterpriseId, fontSize)
      );

      if (!newImageUrl) {
        this._showAlertModel('URL d\'image manquante dans la réponse', 'warning');
        return;
      }

      if (newImageUrl === imageUrl) {
        this._showAlertModel('⚠️ Aucune modification détectée', 'warning');
      } else {
        const stripoCdnUrl = await this.uploadToStripoDelegate(newImageUrl);
        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .setAttribute('style', '')
          .apply(new ModificationDescription('Image remplacée après ajout du modèle produit').withParams({ url: stripoCdnUrl }));
        this._showAlertModel('✅ Modèle produit appliqué', 'success');
      }
    } catch (error) {
      console.error('Erreur API modèle produit :', error);
      this._showAlertModel('❌ Erreur lors de l\'ajout du modèle', 'error');
    } finally {
      this._restoreModelButton();
    }
  }

  private async onAddLogoOnImage(): Promise<void> {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const imgNode = this.selectedNode.querySelector?.('img');
    const aNode = this.selectedNode.querySelector?.('a[href]');

    if (!imgNode || !aNode || !isElementNode(imgNode) || !isElementNode(aNode)) {
      console.warn('Image ou lien manquant ou invalide');
      return;
    }

    const imageUrl = imgNode.getAttribute('src') || '';
    const htmlUrl = aNode.getAttribute('href') || '';
    const width = imgNode.getAttribute('width') || '100%';

    if (!imageUrl || !htmlUrl) {
      console.warn('Image ou lien manquant (attributs)');
      return;
    }

    this.api.setUIEAttribute('addLogoButton', UEAttr.BUTTON.caption, '⏳ Ajout du logo...');
    this.api.setUIEAttribute('addLogoButton', UEAttr.BUTTON.disabled, true);

    try {
      const newImageUrl: string = await lastValueFrom(
        this.generatorService.addLogoOnImage(imageUrl, htmlUrl, width)
      );

      if (!newImageUrl) {
        this._showAlertLogo('URL d\'image manquante dans la réponse', 'warning');
        return;
      }

      if (newImageUrl === imageUrl) {
        this._showAlertLogo('⚠️ Aucune modification détectée', 'warning');
      } else {
        const stripoCdnUrl = await this.uploadToStripoDelegate(newImageUrl);
        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .setAttribute('style', '')
          .apply(new ModificationDescription('Image remplacée après l\'ajout du logo').withParams({ url: stripoCdnUrl }));
        this._showAlertLogo('✅ Logo appliqué', 'success');
      }
    } catch (error) {
      console.error('Erreur API logo :', error);
      this._showAlertLogo('❌ Erreur lors de l\'ajout du logo', 'error');
    } finally {
      this._restoreLogoButton();
    }
  }

  private _showAlertRemise(message: string, type: 'info' | 'warning' | 'error' | 'success') {
    // Affiche le message
    this.api.updateValues({ customRemiseAlert: message });
    this.api.setUIEAttribute('customRemiseAlert', 'type', type);
    // Rends le message visible (display block)
    this.api.setVisibility('customRemiseAlert', true);

    // 🕒 Cache l'alerte automatiquement après 4 secondes
    setTimeout(() => {
      // Masque le message
      this.api.setVisibility('customRemiseAlert', false);
      // Optionnel : vide le texte
      this.api.updateValues({ customRemiseAlert: '' });
    }, 4000);
  }

  private _showAlertResize(message: string, type: 'info' | 'warning' | 'error' | 'success') {
    // Affiche le message
    this.api.updateValues({ customResizeAlert: message });
    this.api.setUIEAttribute('customResizeAlert', 'type', type);
    // Rends le message visible (display block)
    this.api.setVisibility('customResizeAlert', true);

    // 🕒 Cache l'alerte automatiquement après 4 secondes
    setTimeout(() => {
      // Masque le message
      this.api.setVisibility('customResizeAlert', false);
      // Optionnel : vide le texte
      this.api.updateValues({ customResizeAlert: '' });
    }, 4000);
  }

  private _showAlertRemoveBAckground(message: string, type: 'info' | 'warning' | 'error' | 'success') {
    // Affiche le message
    this.api.updateValues({ RemoveBAckgroundAlert: message });
    this.api.setUIEAttribute('RemoveBAckgroundAlert', 'type', type);
    // Rends le message visible (display block)
    this.api.setVisibility('RemoveBAckgroundAlert', true);

    // 🕒 Cache l'alerte automatiquement après 4 secondes
    setTimeout(() => {
      // Masque le message
      this.api.setVisibility('RemoveBAckgroundAlert', false);
      // Optionnel : vide le texte
      this.api.updateValues({ RemoveBAckgroundAlert: '' });
    }, 4000);
  }

  private async onmajRemiseProduct(): Promise<void> {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const imgNode = this.selectedNode.querySelector?.('img');
    const aNode = this.selectedNode.querySelector?.('a[href]');

    if (!imgNode || !aNode || !isElementNode(imgNode) || !isElementNode(aNode)) {
      console.warn('Image ou lien manquant ou invalide');
      return;
    }

    const imageUrl = imgNode.getAttribute('src') || '';
    const htmlUrl = aNode.getAttribute('href') || '';
    const width = imgNode.getAttribute('width') || '100%';

    if (!imageUrl || !htmlUrl) {
      console.warn('Image ou lien manquant (attributs)');
      return;
    }

    // ⬇️ Get UI values for the color, prefer palette over manual if both are set
    const values = (this as any).api?.getValues?.() ?? {};
    const rawPick = (values['remiseColorPicker'] ?? '') as string;
    const rawCustomText = (values['remiseTextInput'] ?? '') as string;

    // Trim + validate (#RGB or #RRGGBB). If invalid/empty => null.
    const bgColor = this._sanitizeHex(rawPick);
    const customText = rawCustomText.trim() || undefined;

    // Disable button + spinner
    this.api.setUIEAttribute('customButton', UEAttr.BUTTON.caption, '⏳ Mise à jour...');
    this.api.setUIEAttribute('customButton', UEAttr.BUTTON.disabled, true);

    try {
      // Pass bgColor and customText only if they exist
      const newImageUrl: string = await lastValueFrom(
        this.generatorService.majRemiseProduct(imageUrl, htmlUrl, width, bgColor ?? undefined, customText)
      );

      if (!newImageUrl) {
        console.warn('URL d’image manquante dans la réponse');
        this._showAlertRemise('URL d’image manquante dans la réponse', 'warning');
        this._restoreCustomButton();
        return;
      }

      if (newImageUrl === imageUrl) {
        this._showAlertRemise('⚠️ Aucune modification détectée', 'warning');
      } else {
        const stripoCdnUrl = await this.uploadToStripoDelegate(newImageUrl);
        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .setAttribute('style', '')
          .apply(new ModificationDescription('Image remplacée après mise à jour').withParams({ url: stripoCdnUrl }));
        this._showAlertRemise('✅ Remise appliquée', 'success');
      }
    } catch (error) {
      console.error('Erreur API remise :', error);
      this._showAlertRemise('❌ Erreur lors de la mise à jour', 'error');
    } finally {
      this._restoreCustomButton();
    }
  }

  private onResizeImageButtonClick(jElement: any): void {
    const imageElement = jElement.find('img');
    const imageUrl = imageElement.attr('src');
    const supportedFormats = ['jpeg', 'jpg', 'png', 'webp'];
    const imageExtension = imageUrl.split('.').pop().toLowerCase();

    if (!supportedFormats.includes(imageExtension)) {
      this.errorMessage = "Uniquement les formats jpeg, png et webp sont supportés pour le redimensionnement IA";
      this.dialogService.open(this.dialogErrorAnimate);
      return;
    }

    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      this.dialogService.open(ResizeImageModalComponent, {
        context: {
          imageUrl,
          imgWidth,
          imgHeight,
          jElement,
        },
      }).onClose.subscribe(result => {

        if (result) {
          this.afterResizeImageButtonClick(result.jElement, result.newWidth, result.newHeight, result.imageUrl, result.prompt);
        }
      });
    };
  }

  private afterResizeImageButtonClick(
    jElement: any,
    newWidth: number,
    newHeight: number,
    imageUrl: string,
    prompt: string
  ): void {
    const imageElement = jElement.find('img');
    ;
    const selectedNode = this.selectedNode;
    if (!isNaN(newWidth) && !isNaN(newHeight)) {

      const dialogRef = this.dialogService.open(FireflyChoiceComponent, {
        context: { imageUrls: [] },
        hasBackdrop: true,
        closeOnBackdropClick: false,
        closeOnEsc: false
      });

      this.resize(imageUrl, newWidth, newHeight, imageElement, prompt);

      this.imageSelectionService.imageUrls$.subscribe(urls => {
        if (!urls || urls.length === 0) {
          return; // rien à afficher pour l'instant
        }

        dialogRef.componentRef.instance.imageUrls = urls;

        dialogRef.onClose.subscribe(async (selectedImageUrl: string) => {


          if (selectedImageUrl && selectedNode && isElementNode(selectedNode)) {
            const loadingToast = this.toastService.loading("Ajout de l'image en cours", '');
            imageElement.attr('src', selectedImageUrl);
            const imgNode = selectedNode.querySelector('img'); // ✅ ciblage correct
            if (imgNode) {
              try {
                const stripoCdnUrl = await this.uploadToStripoDelegate(selectedImageUrl);
                this.api.getDocumentModifier()
                  .modifyHtml(imgNode)
                  .setAttribute('src', stripoCdnUrl)
                  .apply(
                    new ModificationDescription('Image remplacée par IA : {url}')
                      .withParams({ url: stripoCdnUrl })
                  );
              } catch (e) {
                this.toastService.error('Erreur', 'Impossible de téléverser l’image.');
              } finally {
                this.toastService.hideLoading(loadingToast);
                this.toastService.success('Image remplacée', 'L’image IA a bien été intégrée.');
              }
            }
            else {
              this.toastService.error('Erreur', 'Aucune image à remplacer');
              console.warn('[DEBUG] Aucun <img> trouvé dans selectedNode');
            }
          }

        });

      });
    } else {
      console.error('Largeur ou hauteur invalide pour le redimensionnement IA.');
    }
  }
  private async _getStripoHtmlAndCss(): Promise<{ html: string; css: string }> {
    return new Promise((resolve, reject) => {
      const actionsApi = (window as any).StripoEditorApi?.actionsApi;

      if (!actionsApi?.getTemplateData) {
        console.warn('⚠️ StripoEditorApi non disponible dans l’extension');
        reject('StripoEditorApi non disponible');
        return;
      }

      actionsApi.getTemplateData((data: {
        html: string;
        css: string;
        width?: number;
        height?: number;
        utmParams?: any;
        syncModulesIds?: string[];
      }) => {
        if (!data?.html) {
          reject('HTML vide');
        } else {
          resolve({ html: data.html, css: data.css });
        }
      });
    });
  }
  private async _extractCssDimensionFromStripo(classList: string[]):
    Promise<{ width?: string; height?: string }> {

    try {
      const { css } = await this._getStripoHtmlAndCss();

      if (!css) {
        console.warn('❌ Aucun CSS trouvé dans le template Stripo');
        return {};
      }


      // Supprimer les @media
      const cssWithoutMedia = css.replace(/@media[^{]+\{([\s\S]+?\})\s*\}/g, '');

      // Tester chaque classe individuellement
      for (const cls of classList) {
        const regex = new RegExp(`\\.${cls} \\s *\\{ [^}]*\\}`, 'g');
        const match = cssWithoutMedia.match(regex);

        if (match && match.length > 0) {
          const cssBlock = match[0];

          const widthMatch = cssBlock.match(/width\s*:\s*([0-9.]+)px/i);
          const heightMatch = cssBlock.match(/height\s*:\s*([0-9.]+)px/i);

          const width = widthMatch ? widthMatch[1] : undefined;
          const height = heightMatch ? heightMatch[1] : undefined;

          if (width || height) {

            return { width, height };
          }
        }
      }

      console.warn(`⚠️ Aucune règle CSS trouvée pour ces classes: `, classList);
    } catch (err) {
      console.error('❌ Erreur lors de l’extraction CSS Stripo :', err);
    }

    return {};
  }





  private async onRecadrageProduit(): Promise<void> {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const imgNode = this.selectedNode.querySelector?.('img');
    if (!imgNode || !isElementNode(imgNode)) {
      console.warn('Image manquante ou invalide');
      this._showAlertResize('❌ Aucune image sélectionnée', 'error');
      return;
    }

    const imageUrl = imgNode.getAttribute('src') || '';
    const html = imgNode.getOuterHTML();
    const classMatch = html.match(/class=["']([^"']+)["']/i);
    const classList = classMatch ? classMatch[1].split(/\s+/) : [];


    // 🔹 1️⃣ Récupération des dimensions HTML si présentes
    let width_block = imgNode.getAttribute('width');
    let height_block = imgNode.getAttribute('height');

    // 🔹 2️⃣ Si absentes, lire les dimensions calculées dans le CSS desktop
    if ((!width_block || width_block === 'auto') && classList) {
      const cssDims = await this._extractCssDimensionFromStripo(classList);
      if (cssDims.width) width_block = cssDims.width;
      if (cssDims.height) height_block = cssDims.height;
    }


    // 🔹 Valeurs par défaut au cas où
    const w = parseFloat(width_block || '300');
    const h = parseFloat(height_block || '300');

    width_block = String(isNaN(w) ? 300 : w * 2);
    height_block = String(isNaN(h) ? 300 : h * 2);



    // 🔸 Alerte et désactivation du bouton
    this.api.setUIEAttribute('quickResize', UEAttr.BUTTON.caption, '⏳ Recadrage...');
    this.api.setUIEAttribute('quickResize', UEAttr.BUTTON.disabled, true);

    try {
      const newImageUrl = await lastValueFrom(
        this.generatorService.recadrageProduct(imageUrl, width_block, height_block)
      ) as unknown as string;

      if (!newImageUrl) {
        this._showAlertResize('❌ Aucune image retournée par le service', 'error');
        return;
      }

      if (newImageUrl === imageUrl) {
        this._showAlertResize('⚠️ L’image est déjà au bon ratio', 'warning');
      } else {
        const stripoCdnUrl = await this.uploadToStripoDelegate(newImageUrl);

        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .setAttribute('style', '')
          .apply(new ModificationDescription('Image recadrée').withParams({ url: stripoCdnUrl }));

        this._showAlertResize('✅ Image recadrée avec succès', 'success');
      }
    } catch (error) {
      console.error('Erreur lors du recadrage :', error);
      this._showAlertResize('❌ Erreur lors du recadrage', 'error');
    } finally {
      this._restoreRecadrageButton();
    }
  }


  private _restoreRecadrageButton(): void {
    this.api.setUIEAttribute('quickResize', UEAttr.BUTTON.caption, '✨ Redimensionnement rapide');
    this.api.setUIEAttribute('quickResize', UEAttr.BUTTON.disabled, false);
  }


  private async onRemoveBAckgroud(): Promise<void> {
    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const imgNode = this.selectedNode.querySelector?.('img');
    if (!imgNode || !isElementNode(imgNode)) {
      console.warn('Image manquante ou invalide');
      this._showAlertRemoveBAckground('❌ Aucune image sélectionnée', 'error');
      return;
    }

    const imageUrl = imgNode.getAttribute('src') || '';
    // ⬇️ Get UI values for the color, prefer palette over manual if both are set
    const values = (this as any).api?.getValues?.() ?? {};
    const rawPick = (values['removeBackgroundPicker'] ?? null) as string;

    // Trim + validate (#RGB or #RRGGBB). If invalid/empty => null.
    const bgColor = this._sanitizeHex(rawPick);

    if (!imageUrl) {
      console.warn('URL de l’image manquante');
      this._showAlertRemoveBAckground('❌ L’URL de l’image est manquante', 'error');
      return;
    }

    // 🔸 Affiche une alerte et désactive le bouton pendant le traitement
    this.api.setUIEAttribute('removeBackground', UEAttr.BUTTON.caption, '⏳ Détourage...');
    this.api.setUIEAttribute('removeBackground', UEAttr.BUTTON.disabled, true);

    try {
      // 🔹 Appel du service de recadrage
      const newImageUrl = await lastValueFrom(
        this.generatorService.removeBackgroud(imageUrl, bgColor)
      ) as unknown as string;


      if (!newImageUrl) {
        console.warn('Aucune URL retournée par le service');
        this._showAlertRemoveBAckground('❌ Aucune image retournée par le service', 'error');
        return;
      }

      if (newImageUrl === imageUrl) {
        this._showAlertRemoveBAckground('⚠️ L’image est déjà détourée', 'warning');
      } else {
        // 🔹 Upload vers le CDN Stripo si besoin
        const stripoCdnUrl = await this.uploadToStripoDelegate(newImageUrl);

        // 🔹 Mise à jour dans l’éditeur
        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .setAttribute('style', '')
          .apply(new ModificationDescription('Image détouré').withParams({ url: stripoCdnUrl }));

        this._showAlertRemoveBAckground('✅ Image détouré avec succès', 'success');
      }

    } catch (error) {
      console.error('Erreur lors du recadrage :', error);
      this._showAlertRemoveBAckground('❌ Erreur lors du recadrage', 'error');
    } finally {
      this._restoreRemovebackgroundButton();
    }
  }

  private _restoreRemovebackgroundButton(): void {
    this.api.setUIEAttribute('removeBackground', UEAttr.BUTTON.caption, '✂️ Détourage image');
    this.api.setUIEAttribute('removeBackground', UEAttr.BUTTON.disabled, false);
  }

  public resize(imageUrl: string, newWidth: number, newHeight: number, imageElement: any, prompt: string): void {
    const ratio = newWidth / newHeight;
    this.imageSelectionService.resetImageUrls();

    this.generatorService.resizeImageRatio(imageUrl, ratio, prompt).subscribe( //! ajouter prompt ici 
      response => {
        // Mise à jour des URLs des images une fois la réponse reçue
        this.imageSelectionService.updateImageUrls([response['url'], response['url_1'], response['url_2']]);

      },
      error => {
        console.error('Erreur lors du redimensionnement de l\'image :', error);
      }
    );
  }



  private onImageToGifButtonClick(jElement: any): void {
    const imageElement = jElement.find('img');
    const imageUrl = imageElement.attr('src')?.split('?')[0];
    const width_block = imageElement.attr('width');

    const supportedFormats = ['jpeg', 'jpg', 'png', 'webp'];
    const imageExtension = imageUrl?.split('.').pop()?.toLowerCase();

    if (!supportedFormats.includes(imageExtension)) {
      this.errorMessage = "Uniquement les formats jpeg, png et webp sont supportés pour l'animation.";
      this.dialogRef = this.dialogService.open(this.dialogErrorAnimate);
      return;
    }

    const dialogRef = this.dialogService.open(ImageToGifDialogComponent, {
      context: {
        imageUrl,
        width_block
      },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
      dialogClass: 'custom-dialog'
    });

    dialogRef.componentRef.instance.imageUrl = imageUrl;

    dialogRef.onClose.subscribe(async (result: string) => {

      if (result && result.startsWith('http')) {
        const loadingToast = this.toastService.loading("Ajout de l'image en cours", "Le GIF est en cours d'intégration à l'eamil");
        imageElement.attr('src', result);

        // Appliquer aussi à selectedNode via API Stripo (comme pour IA resize)
        const selectedNode = this.selectedNode;
        const imgNode = selectedNode?.querySelector?.('img');


        if (selectedNode && imgNode) {
          try {
            const stripoCdnUrl = await this.uploadToStripoDelegate(result);
            this.api.getDocumentModifier()
              .modifyHtml(imgNode)
              .setAttribute('src', stripoCdnUrl)
              .apply(new ModificationDescription('Image animée (GIF) appliquée'));
          }
          catch (e) {
            this.toastService.error('Erreur', 'Impossible de téléverser l’image.');
          } finally {
            this.toastService.hideLoading(loadingToast);
            this.toastService.success('Image remplacée', 'L’image animée a bien été intégrée.');
          }
        }
      }
    });
  }

  private onAmbianceButtonClick(jElement: any): void {
    const imageUrl = jElement.find('img').attr('src')?.split('?')[0];
    if (!imageUrl) return;
    // page produit du lien de l'image : sert à scraper les autres vues du produit
    const productUrl: string = jElement.find('a[href]').first().attr('href') || '';

    const dialogRef = this.dialogService.open(ImageAmbianceDialogComponent, {
      context: { imageUrl, productUrl },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
      dialogClass: 'custom-dialog'
    });

    dialogRef.onClose.subscribe(async (result: string) => {
      const imgNode = this.selectedNode?.querySelector?.('img');
      if (!result || !imgNode) return;

      const loadingToast = this.toastService.loading("Ajout de l'image en cours", "L'image mise en ambiance est en cours d'intégration à l'email");
      try {
        const stripoCdnUrl = await this.uploadToStripoDelegate(result);
        this.api.getDocumentModifier()
          .modifyHtml(imgNode)
          .setAttribute('src', stripoCdnUrl)
          .apply(new ModificationDescription('Image mise en ambiance appliquée'));
        this.toastService.success('Image remplacée', "L'image mise en ambiance a bien été intégrée.");
      } catch (e) {
        this.toastService.error('Erreur', 'Impossible de téléverser l’image.');
      } finally {
        this.toastService.hideLoading(loadingToast);
      }
    });
  }




}

export class WriteOnImageControl extends Control {
  private selectedNode: ImmutableHtmlNode | null = null;
  public generatorService!: GeneratorService;
  public imageSelectionService!: ImageSelectionService;
  public dialogService!: NbDialogService;
  public dialogErrorAnimate!: TemplateRef<any>;
  public errorMessage = '';
  dialogRef: NbDialogRef<any> | undefined = undefined;
  public emailId!: string;
  public userId!: number;
  public userName!: string;
  public uploadToStripoDelegate!: (url: string) => Promise<string>;

  constructor() {
    super();
  }

  override getId() {
    return 'write-on-image-control';
  }

  override getTemplate() {
    return `
    <div style="padding: 10px 20px;">
      <div style="margin-bottom: 5px;">
        <${UIElementType.LABEL}
            ${UEAttr.LABEL.text}="Choisissez la couleur du texte : (optionnel)">
        </${UIElementType.LABEL}>
      </div>

      <div style="margin-bottom: 5px;">
        <${UIElementType.COLOR}
            ${UEAttr.DEFAULT.name}="writeOnImageColorPicker" >
        </${UIElementType.COLOR}>
      </div>

      <div style="margin-bottom: 5px;">
        <${UIElementType.TEXT}
            ${UEAttr.TEXT.name}="textUp"
            ${UEAttr.TEXT.placeholder}="Texte haut">
        </${UIElementType.TEXT}>
      </div>

      <div style="margin-bottom: 10px;">
        <${UIElementType.TEXT}
              ${UEAttr.TEXT.name}="textDown"
              ${UEAttr.TEXT.placeholder}="Texte bas">
        </${UIElementType.TEXT}>
      </div>

      <div>
        <${UIElementType.MESSAGE}
            name="customwriteOnImageAlert"
            type="info"
            style="margin-top: 5px;">
        </${UIElementType.MESSAGE}>

        <${UIElementType.BUTTON}
              ${UEAttr.BUTTON.caption}="✏️ Écrire sur l'image"
              ${UEAttr.BUTTON.name}="writeOnImageButton"
              style="margin-top: 10px;">
        </${UIElementType.BUTTON}>
      </div>

      <hr style="border: none; border-top: 1px solid #fff; margin: 10px 0;">

      <div style="margin-bottom: 10px;">
        <${UIElementType.TEXT}
            ${UEAttr.TEXT.name}="textCTA"
            ${UEAttr.TEXT.placeholder}="Texte du CTA">
        </${UIElementType.TEXT}>
      </div>

      <div style="margin-bottom: 5px;">
        <${UIElementType.LABEL}
            ${UEAttr.LABEL.text}="Couleur de fond du CTA : (optionnel)">
        </${UIElementType.LABEL}>
      </div>

      <div style="margin-bottom: 10px;">
        <${UIElementType.COLOR}
            ${UEAttr.DEFAULT.name}="ctaBackgroundColorPicker">
        </${UIElementType.COLOR}>
      </div>

      <div>
        <${UIElementType.MESSAGE}
            name="customCTAAlert"
            type="info">
        </${UIElementType.MESSAGE}>

        <${UIElementType.BUTTON}
            ${UEAttr.BUTTON.caption}="✏️ Ajouter CTA"
            ${UEAttr.BUTTON.name}="WriteCTAOnImage">
        </${UIElementType.BUTTON}>
      </div>

    </div>
    `;
  }



  override onRender() {
    this.api.onValueChanged('writeOnImageButton', () => this._onWriteOnImageButtonClickFromSelectedNode());
    this.api.onValueChanged('WriteCTAOnImage', () => this._WriteCTAOnImageFromSelectedNode());
    this.api.updateValues({ customwriteOnImageAlert: '', customCTAAlert: '' });
    this._hideEmptyAlerts();
  }

  // Même problème que CustomToolbarButtonControl : Stripo reconstruit le panneau sans
  // repasser par onRender et les MESSAGE reviennent visibles et vides (encarts bleus).
  private _hideEmptyAlerts(): void {
    const values = this.api.getValues();
    for (const name of ['customwriteOnImageAlert', 'customCTAAlert']) {
      if (!values[name]) {
        this.api.setVisibility(name, false);
      }
    }
  }

  override onTemplateNodeUpdated(node: ImmutableHtmlNode): void {
    this.selectedNode = node;
    this._hideEmptyAlerts();
  }

  private _onWriteOnImageButtonClickFromSelectedNode(): void {

    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    const outerHTML = this.selectedNode.getOuterHTML();
    const jElement = $(outerHTML); // clone jQuery
    this.onWriteOnImageButtonClick(jElement);
  }

  private _WriteCTAOnImageFromSelectedNode(): void {

    if (!this.selectedNode || !isElementNode(this.selectedNode)) return;

    this.api.setUIEAttribute('WriteCTAOnImage', UEAttr.BUTTON.caption, '⏳ Création CTA en cours...');
    this.api.setUIEAttribute('WriteCTAOnImage', UEAttr.BUTTON.disabled, true);

    const outerHTML = this.selectedNode.getOuterHTML();
    const jElement = $(outerHTML); // clone jQuery
    this.WriteCTAOnImage(jElement);
  }

  private _resetCTAButton(): void {
    this.api.setUIEAttribute('WriteCTAOnImage', UEAttr.BUTTON.caption, '✏️ Ajouter CTA');
    this.api.setUIEAttribute('WriteCTAOnImage', UEAttr.BUTTON.disabled, false);
  }


  private onWriteOnImageButtonClick(jElement: any): void {
    const imageElement = jElement.find('img');
    const imageUrl = imageElement.attr('src');
    const width = imageElement.attr('width') || '100%';
    const values = this.api.getValues() as { [key: string]: any };


    const textUp = (values['textUp'] ?? '').toString().trim();
    const textDown = (values['textDown'] ?? '').toString().trim();

    if (!textUp && !textDown) {

      this.api.updateValues({ customwriteOnImageAlert: "Veuilez ajouter du texte" });
      this.api.setUIEAttribute("customwriteOnImageAlert", 'type', "warning");
      // Rends le message visible (display block)
      this.api.setVisibility("customwriteOnImageAlert", true);

      // 🕒 Cache l'alerte automatiquement après 4 secondes
      setTimeout(() => {
        // Masque le message
        this.api.setVisibility("customwriteOnImageAlert", false);
        // Optionnel : vide le texte
        this.api.updateValues({ "customwriteOnImageAlert": '' });
      }, 2000);
      console.warn('Texte haut ou bas manquant');
      return;
    }

    // Si un seul des deux textes est rempli, on est en mode "texte unique"
    const single_text = (!textUp || !textDown) ? 'true' : 'false';

    imageElement.after(`
  < div class="esd-banner-generating" style = "left: 0px; width: ${width};" >
    <div class="loader-c" >
      <div class="loader-z" > </div>
        </div>
        </div>
          `);

    const originalImageUrl = imageUrl;

    const dialogRef = this.dialogService.open(WriteOnImageComponent, {
      context: { staticImageUrl: '' },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false
    });

    const textColor = (values['writeOnImageColorPicker'] ?? '').toString().trim();

    (this.generatorService.writeOnImage(imageUrl, textUp, textDown, single_text, textColor) as Observable<any>).subscribe(
      (response: any) => {
        if (response.statut === 'ok') {
          this.imageSelectionService.updateImageOptions([
            response.text_image_center,
            response.text_image_left,
            response.text_image_right
          ]);
          dialogRef.componentRef.instance.staticImageUrl = response.resized_original_url;
        }
      },
      (error: any) => {
        console.error('Erreur API écriture image', error);
        this.dialogRef = this.dialogService.open(this.dialogErrorAnimate);
        dialogRef.close();
        imageElement.next('.esd-banner-generating').remove();
        imageElement.show();
      }
    );

    dialogRef.onClose.subscribe(async (result: { mergedImageUrl?: string } | null) => {
      if (result?.mergedImageUrl) {
        imageElement.attr('src', result.mergedImageUrl);


        const selectedNode = this.selectedNode;
        const imgNode = selectedNode?.querySelector?.('img');
        if (imgNode) {

          const stripoCdnUrl = await this.uploadToStripoDelegate(result.mergedImageUrl);

          this.api.getDocumentModifier()
            .modifyHtml(imgNode)
            .setAttribute('src', stripoCdnUrl)
            .apply(new ModificationDescription("Image modifiée avec texte"));

        }
      } else {
        if (result === undefined) {
          this.dialogRef = this.dialogService.open(this.dialogErrorAnimate);
        } else {
          imageElement.attr('src', originalImageUrl);
        }
      }
      imageElement.next('.esd-banner-generating').remove();
      imageElement.show();
    });
  }

  /**
   * Le service ne renvoie plus le visuel fini mais le CTA seul, en PNG
   * transparent : on ouvre la modale de placement (même flux que l'écriture sur
   * image) et c'est la fusion qui produit le visuel final.
   */
  private WriteCTAOnImage(jElement: any): void {
    const imageElement = jElement.find('img');
    const imageUrl = imageElement.attr('src');
    const width = imageElement.attr('width') || '100%';

    const values = this.api.getValues() as { [key: string]: any };
    const textCta = (values['textCTA'] ?? '').toString().trim();
    const backgroundColor = (values['ctaBackgroundColorPicker'] ?? '').toString().trim();

    if (!textCta) {
      console.error('Erreur : Texte CTA vide');
      this.api.updateValues({ customCTAAlert: "Veuilez ajouter du texte" });
      this.api.setUIEAttribute("customCTAAlert", 'type', "warning");
      this.api.setVisibility("customCTAAlert", true);

      // 🕒 Cache l'alerte automatiquement après 2 secondes
      setTimeout(() => {
        this.api.setVisibility("customCTAAlert", false);
        this.api.updateValues({ "customCTAAlert": '' });
      }, 2000);
      this._resetCTAButton();
      return;
    }

    // Affiche un loader sous l'image
    imageElement.after(`
      < div class="esd-banner-generating" style = "left: 0px; width: ${width};" >
        <div class="loader-c" >
          <div class="loader-z" > </div>
            </div>
            </div>
              `);

    const originalImageUrl = imageUrl;

    const dialogRef = this.dialogService.open(WriteCtaOnImageComponent, {
      context: { staticImageUrl: '' },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false
    });

    this.generatorService.WriteCTAOnImage(imageUrl, textCta, backgroundColor).subscribe(
      (response) => {
        this._resetCTAButton();
        if (response?.statut === 'ok') {
          dialogRef.componentRef.instance.setCtaOptions(
            response.resized_original_url,
            buildCtaOptions(response)
          );
        } else {
          console.error('Génération du CTA en échec :', response);
          dialogRef.close();
        }
      },
      (error: any) => {
        console.error('Erreur lors de la génération du CTA :', error);
        this._resetCTAButton();
        dialogRef.close();
      }
    );

    dialogRef.onClose.subscribe(async (result: { mergedImageUrl?: string } | null) => {
      if (result?.mergedImageUrl) {
        imageElement.attr('src', result.mergedImageUrl);

        const imgNode = this.selectedNode?.querySelector?.('img');
        if (imgNode) {
          const stripoCdnUrl = await this.uploadToStripoDelegate(result.mergedImageUrl);

          this.api.getDocumentModifier()
            .modifyHtml(imgNode)
            .setAttribute('src', stripoCdnUrl)
            .apply(new ModificationDescription("Image modifiée avec CTA"));
        }
      } else {
        if (result === undefined) {
          this.dialogRef = this.dialogService.open(this.dialogErrorAnimate);
        } else {
          imageElement.attr('src', originalImageUrl);
        }
      }
      imageElement.next('.esd-banner-generating').remove();
      imageElement.show();
    });
  }


}




